import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Briefcase, Plus, Loader2, Trash2, Eye, Building2, Calendar, FileText,
  CheckCircle, Clock, Archive, ClipboardCheck, AlertCircle, Ban, Check,
  ArrowUp, ArrowDown, ArrowUpDown, ArrowDownCircle, ArrowUpCircle, Search, AlertTriangle, Upload, X, File, Download,
  DollarSign, CreditCard, ListChecks, Receipt, User, Send, MessageSquare, Inbox, XCircle, PlayCircle,
  Edit, Save, Pencil, ShoppingCart, ClipboardList, Wrench, HardHat, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Import full-featured transaction modals
// (Not used - buttons now navigate to respective pages)

// Import Purchasing Components for Tabs
import PurchasingServices from '../purchasing/PurchasingServices';
import PurchasingSubcontractors from '../purchasing/PurchasingSubcontractors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

const AccountingJobs = ({ isBoardView = false }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [correctionJobs, setCorrectionJobs] = useState([]);
  const [cancelledJobs, setCancelledJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [managerRequests, setManagerRequests] = useState([]); // Yöneticiden istenen işler
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [archiveSubTab, setArchiveSubTab] = useState('cancelled');
  const [jobSubTab, setJobSubTab] = useState('pending'); // Alt sekme için yeni state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Talepler state'leri
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]); // Kullanıcının kendi talepleri
  const [taleplerSubTab, setTaleplerSubTab] = useState('gelen');
  const [selectedTalep, setSelectedTalep] = useState(null);
  const [talepActionLoading, setTalepActionLoading] = useState(false);
  const [talepActionNote, setTalepActionNote] = useState('');
  const [talepSortConfig, setTalepSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [convertToJobModalOpen, setConvertToJobModalOpen] = useState(false); // Onayla ve İşe Çevir modal
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [managerRequestModalOpen, setManagerRequestModalOpen] = useState(false); // Yöneticiden iş talebi modalı
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedManagerRequest, setSelectedManagerRequest] = useState(null);
  
  // İşlem modalları için state'ler (Onaylanan işler için)
  const [jobActionModal, setJobActionModal] = useState({ open: false, type: null }); // Tek state objesi
  const [jobActionData, setJobActionData] = useState({});
  const [jobActionSubmitting, setJobActionSubmitting] = useState(false);
  const [pendingTransactions, setPendingTransactions] = useState([]); // Bekleyen işlemler (henüz kaydedilmemiş)
  const [editingTransaction, setEditingTransaction] = useState(null); // Düzenlenen işlem
  const [completeJobModalOpen, setCompleteJobModalOpen] = useState(false);
  const [completeJobNote, setCompleteJobNote] = useState('');
  
  // Yapılacak İşler için belge ekleme
  const [addDocumentsModalOpen, setAddDocumentsModalOpen] = useState(false);
  const [todoDocuments, setTodoDocuments] = useState([]);
  const [todoDocumentsSubmitting, setTodoDocumentsSubmitting] = useState(false);
  // Yönetim Kurulu onay/red modalı
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingJobId, setRejectingJobId] = useState(null);
  
  // Form state for new job
  const [formData, setFormData] = useState({
    konu: '',
    aciklama: '',
    belgeler: [],
    sube_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Form state for correction
  const [correctionFormData, setCorrectionFormData] = useState({
    konu: '',
    aciklama: '',
    belgeler: []
  });
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  
  // Form state for manager request
  const [managerRequestFormData, setManagerRequestFormData] = useState({
    konu: '',
    aciklama: '',
    oncelik: 'normal',
    belgeler: [],
    answers: {}
  });
  const [managerRequestSubmitting, setManagerRequestSubmitting] = useState(false);
  
  // Yöneticiden istenen işler için sorular
  const managerRequestQuestions = [
    { id: 'ne_isteniyor', label: 'Ne isteniyor?', type: 'textarea', required: true },
    { id: 'neden_gerekli', label: 'Neden gerekli?', type: 'textarea', required: false },
    { id: 'ne_zaman', label: 'Ne zamana kadar gerekli?', type: 'text', required: false },
    { id: 'ek_bilgi', label: 'Ek bilgi', type: 'textarea', required: false }
  ];
  
  // Branch info
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Yeni kayıt sayaç sistemi - localStorage ile
  const [viewedJobs, setViewedJobs] = useState(() => {
    const saved = localStorage.getItem('muhasebe_jobs_viewed');
    const defaultValue = { active: [], pending: [], correction: [], cancelled: [], archived: [], manager_requests: [] };
    if (saved) {
      const parsed = JSON.parse(saved);
      // Eksik key'leri ekle
      return { ...defaultValue, ...parsed };
    }
    return defaultValue;
  });

  // Görüntüleme zamanı takibi
  const [lastViewTime, setLastViewTime] = useState(() => {
    const saved = localStorage.getItem('muhasebe_jobs_lastview');
    const defaultValue = { active: null, pending: null, correction: null, cancelled: null, archived: null, manager_requests: null };
    if (saved) {
      const parsed = JSON.parse(saved);
      // Eksik key'leri ekle
      return { ...defaultValue, ...parsed };
    }
    return defaultValue;
  });

  // Yeni kayıt sayısını hesapla
  const getNewCount = (tabType, data) => {
    const viewedIds = viewedJobs[tabType] || [];
    const lastView = lastViewTime[tabType];
    
    return data.filter(item => {
      // Daha önce görüntülenmemiş
      if (!viewedIds.includes(item.id)) {
        // Ya da son görüntülemeden sonra güncellenmiş
        if (lastView && item.updated_at) {
          return new Date(item.updated_at) > new Date(lastView);
        }
        return true;
      }
      // Görüntülendikten sonra güncellenmiş (red, düzeltme vb.)
      if (item.updated_at && lastView) {
        return new Date(item.updated_at) > new Date(lastView);
      }
      return false;
    }).length;
  };

  // Kayıt görüntülendiğinde işaretle
  const markAsViewed = (tabType, itemId) => {
    setViewedJobs(prev => {
      const updated = { ...prev };
      // undefined kontrolü ekle
      if (!updated[tabType]) {
        updated[tabType] = [];
      }
      if (!updated[tabType].includes(itemId)) {
        updated[tabType] = [...updated[tabType], itemId];
      }
      localStorage.setItem('muhasebe_jobs_viewed', JSON.stringify(updated));
      return updated;
    });
  };

  // Tab değiştiğinde son görüntüleme zamanını güncelle
  const updateLastViewTime = (tabType) => {
    setLastViewTime(prev => {
      const updated = { ...prev, [tabType]: new Date().toISOString() };
      localStorage.setItem('muhasebe_jobs_lastview', JSON.stringify(updated));
      return updated;
    });
  };

  // Tab değişikliğinde sayaçları güncelle
  useEffect(() => {
    if (activeTab === 'active' && jobs.length > 0) {
      updateLastViewTime('active');
    } else if (activeTab === 'correction' && correctionJobs.length > 0) {
      updateLastViewTime('correction');
    } else if (activeTab === 'cancelled' && cancelledJobs.length > 0) {
      updateLastViewTime('cancelled');
    } else if (activeTab === 'archive' && archivedJobs.length > 0) {
      updateLastViewTime('archived');
    }
  }, [activeTab, jobs.length, correctionJobs.length, cancelledJobs.length, archivedJobs.length]);

  // Get branches
  useEffect(() => {
    const fetchBranches = async () => {
      setBranchesLoading(true);
      try {
        const response = await fetch(`${API}/branches`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setBranches(data || []);
        }
      } catch (error) {
        console.error('Şubeler yüklenemedi:', error);
      }
      setBranchesLoading(false);
    };
    fetchBranches();
  }, []);

  // Load jobs
  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      // Aktif işleri yükle
      const activeResponse = await fetch(`${API}/accounting/isler?status=active`, {
        headers: getAuthHeaders()
      });
      
      // Hazırlanan (Prepared) işleri yükle
      const preparedResponse = await fetch(`${API}/accounting/isler?status=prepared`, {
        headers: getAuthHeaders()
      });
      
      // Approved (Onaylanan) işleri yükle
      const approvedResponse = await fetch(`${API}/accounting/isler?status=approved`, {
        headers: getAuthHeaders()
      });
      
      // Todo işleri yükle
      const todoResponse = await fetch(`${API}/accounting/isler?status=todo`, {
        headers: getAuthHeaders()
      });
      
      let allJobs = [];
      
      if (activeResponse.ok) {
        const activeData = await activeResponse.json();
        allJobs = [...allJobs, ...(activeData || [])];
      }
      
      if (preparedResponse.ok) {
        const preparedData = await preparedResponse.json();
        allJobs = [...allJobs, ...(preparedData || [])];
      }
      
      if (approvedResponse.ok) {
        const approvedData = await approvedResponse.json();
        allJobs = [...allJobs, ...(approvedData || [])];
      }
      
      if (todoResponse.ok) {
        const todoData = await todoResponse.json();
        allJobs = [...allJobs, ...(todoData || [])];
      }
      
      setJobs(allJobs);
    } catch (error) {
      console.error('İşler yüklenirken hata:', error);
      toast.error('İşler yüklenemedi');
    }
    setLoading(false);
  }, []);

  const loadPendingJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/accounting/isler?status=pending`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPendingJobs(data || []);
      }
    } catch (error) {
      console.error('Onay bekleyen işler yüklenirken hata:', error);
    }
    setLoading(false);
  }, []);

  const loadCorrectionJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/accounting/isler?status=needs_correction`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCorrectionJobs(data || []);
      }
    } catch (error) {
      console.error('Düzeltilmesi gereken işler yüklenirken hata:', error);
    }
    setLoading(false);
  }, []);

  const loadCancelledJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/accounting/isler?status=cancelled`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCancelledJobs(data || []);
      }
    } catch (error) {
      console.error('İptal edilen işler yüklenirken hata:', error);
    }
    setLoading(false);
  }, []);

  const loadArchivedJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/accounting/isler/archived`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setArchivedJobs(data || []);
      }
    } catch (error) {
      console.error('Arşivlenmiş işler yüklenirken hata:', error);
    }
    setLoading(false);
  }, []);

  const loadCompletedJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/accounting/isler?status=completed`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCompletedJobs(data || []);
      }
    } catch (error) {
      console.error('Bitirilen işler yüklenirken hata:', error);
    }
    setLoading(false);
  }, []);

  // Yöneticiden istenen işleri yükle
  const loadManagerRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accounting/manager-job-requests`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setManagerRequests(data || []);
      }
    } catch (error) {
      console.error('Yöneticiden istenen işler yüklenirken hata:', error);
    }
  }, []);

  // Muhasebeye gelen talepleri yükle
  const loadIncomingRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accounting/incoming-requests`, {
        headers: getAuthHeaders()
      });
      setIncomingRequests(response.data || []);
    } catch (error) {
      console.error('Gelen talepler yüklenirken hata:', error);
    }
  }, []);

  // Kullanıcının kendi taleplerini yükle
  const loadMyRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/work/requests/my`, {
        headers: getAuthHeaders()
      });
      setMyRequests(response.data || []);
    } catch (error) {
      console.error('Taleplerim yüklenirken hata:', error);
    }
  }, []);

  // İşleri alt sekmeye göre filtrele
  const getFilteredJobsBySubTab = () => {
    let allJobs = [];
    
    if (jobSubTab === 'todo') {
      allJobs = jobs.filter(j => j.status === 'todo' || j.status === 'new').map(j => ({ ...j }));
    } else if (jobSubTab === 'active') {
      allJobs = jobs.filter(j => j.status !== 'todo' && j.status !== 'new' && j.status !== 'approved').map(j => ({ ...j, status: j.status || 'active' }));
    } else if (jobSubTab === 'approved') {
      allJobs = jobs.filter(j => j.status === 'approved').map(j => ({ ...j }));
    } else if (jobSubTab === 'pending') {
      allJobs = pendingJobs.map(j => ({ ...j, status: 'pending' }));
    } else if (jobSubTab === 'correction') {
      allJobs = correctionJobs.map(j => ({ ...j, status: 'correction' }));
    } else if (jobSubTab === 'completed') {
      allJobs = completedJobs.map(j => ({ ...j, status: 'completed' }));
    } else if (jobSubTab === 'cancelled') {
      allJobs = cancelledJobs.map(j => ({ ...j, status: 'cancelled' }));
    }
    
    // Arama filtresi
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      allJobs = allJobs.filter(job => 
        (job.konu || '').toLowerCase().includes(term) ||
        (job.name || '').toLowerCase().includes(term) ||
        (job.sube_adi || '').toLowerCase().includes(term) ||
        (job.talep_eden || '').toLowerCase().includes(term)
      );
    }
    
    // Tarihe göre sırala (en yeni üstte)
    return allJobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // Talep işlemleri
  const handleTalepAction = async (actionType) => {
    if (!selectedTalep) return;
    
    setTalepActionLoading(true);
    try {
      let newStatus = '';
      
      if (actionType === 'approve') {
        newStatus = 'approved';
      } else if (actionType === 'complete') {
        newStatus = 'completed';
      } else if (actionType === 'reject') {
        newStatus = 'rejected_by_accounting';
      }
      
      await axios.put(`${API}/work/requests/${selectedTalep.id}`, {
        status: newStatus,
        response_message: talepActionNote
      }, { headers: getAuthHeaders() });
      
      const actionMessages = {
        'approve': 'Talep onaylandı',
        'complete': 'Talep tamamlandı',
        'reject': 'Talep reddedildi'
      };
      
      toast.success(actionMessages[actionType]);
      setSelectedTalep(null);
      setTalepActionNote('');
      loadIncomingRequests();
    } catch (error) {
      console.error('Talep işlemi hatası:', error);
      toast.error('Talep işlemi başarısız');
    }
    setTalepActionLoading(false);
  };

  // Talep filtreleme - BranchJobs.js yapısına uygun
  const getFilteredTalepler = () => {
    let filtered;
    if (taleplerSubTab === 'gelen') {
      // Muhasebeye gelen talepler
      filtered = incomingRequests.filter(r => r.status === 'forwarded_to_accounting' || r.status === 'pending_accounting' || r.status === 'pending');
    } else if (taleplerSubTab === 'onayladigim') {
      // Onayladığım talepler (converted_to_job dahil)
      filtered = incomingRequests.filter(r => r.status === 'approved' || r.status === 'converted_to_job' || r.status?.startsWith('forwarded_to_'));
    } else if (taleplerSubTab === 'bitirilen') {
      // Bitirilen talepler (gelen) - completed veya converted_to_job
      filtered = incomingRequests.filter(r => r.status === 'completed' || r.status === 'converted_to_job');
    } else if (taleplerSubTab === 'reddettigim') {
      // Reddettiğim talepler
      filtered = incomingRequests.filter(r => r.status === 'rejected_by_accounting');
    } else if (taleplerSubTab === 'aktif') {
      // Aktif taleplerim (kendi)
      filtered = myRequests.filter(r => !['approved', 'completed', 'converted_to_job', 'rejected', 'rejected_by_branch', 'rejected_by_hr', 'rejected_by_accounting'].includes(r.status));
    } else if (taleplerSubTab === 'onaylanan') {
      // Onaylanan taleplerim (kendi) - converted_to_job dahil
      filtered = myRequests.filter(r => r.status === 'approved' || r.status === 'converted_to_job');
    } else if (taleplerSubTab === 'benim_bitirilen') {
      // Bitirilen taleplerim (kendi) - completed veya converted_to_job
      filtered = myRequests.filter(r => r.status === 'completed' || r.status === 'converted_to_job');
    } else if (taleplerSubTab === 'reddedilen') {
      // Reddedilen taleplerim (kendi)
      filtered = myRequests.filter(r => ['rejected', 'rejected_by_branch', 'rejected_by_hr', 'rejected_by_accounting'].includes(r.status));
    } else {
      filtered = incomingRequests;
    }
    
    // Sıralama
    if (talepSortConfig.key && filtered) {
      return [...filtered].sort((a, b) => {
        let aVal = a[talepSortConfig.key] || '';
        let bVal = b[talepSortConfig.key] || '';
        if (talepSortConfig.key === 'created_at') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }
        if (aVal < bVal) return talepSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return talepSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  };

  const handleTalepSort = (key) => {
    let direction = 'asc';
    if (talepSortConfig.key === key && talepSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTalepSortConfig({ key, direction });
  };

  const getTalepSortIcon = (key) => {
    if (talepSortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return talepSortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-emerald-600" />
      : <ArrowDown className="h-4 w-4 text-emerald-600" />;
  };

  // Yöneticiden iş talebi oluştur
  const handleCreateManagerRequest = async () => {
    if (!managerRequestFormData.konu.trim()) {
      toast.error('İşin konusu zorunludur');
      return;
    }
    
    // En az bir soru cevabı kontrolü
    const answers = managerRequestFormData.answers;
    if (!answers.ne_isteniyor || !answers.ne_isteniyor.trim()) {
      toast.error('"Ne isteniyor?" sorusu zorunludur');
      return;
    }
    
    setManagerRequestSubmitting(true);
    try {
      const response = await fetch(`${API}/accounting/manager-job-requests`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(managerRequestFormData)
      });
      
      if (response.ok) {
        toast.success('İş talebi oluşturuldu');
        setManagerRequestModalOpen(false);
        setManagerRequestFormData({
          konu: '',
          aciklama: '',
          oncelik: 'normal',
          belgeler: [],
          answers: {}
        });
        loadManagerRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Talep oluşturulamadı');
      }
    } catch (error) {
      console.error('İş talebi oluşturma hatası:', error);
      toast.error('Bir hata oluştu');
    }
    setManagerRequestSubmitting(false);
  };

  // Yöneticiden istenen iş talebini sil
  const handleDeleteManagerRequest = async (requestId) => {
    if (!window.confirm('Bu talebi silmek istediğinize emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API}/accounting/manager-job-requests/${requestId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Talep silindi');
        loadManagerRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Talep silinemedi');
      }
    } catch (error) {
      console.error('Talep silme hatası:', error);
      toast.error('Bir hata oluştu');
    }
  };

  // Yöneticiden istenen iş için dosya yükleme
  const handleManagerRequestFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setManagerRequestFormData(prev => ({
          ...prev,
          belgeler: [...prev.belgeler, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: event.target.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Yöneticiden istenen iş için dosya silme
  const removeManagerRequestFile = (index) => {
    setManagerRequestFormData(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  // İş aksiyonu - Giriş Ekle, Çıkış Ekle, Takiplere Kaydet, Çek Ekle, Alacak Ekle, Borç Ekle
  const handleJobAction = (job, actionType) => {
    // Job bilgisini localStorage'a kaydet
    localStorage.setItem('pending_job_action', JSON.stringify({
      job_id: job.id,
      job_konu: job.konu,
      action_type: actionType
    }));
    
    // İlgili sayfaya yönlendir
    switch (actionType) {
      case 'giris':
        navigate('/accounting/income');
        break;
      case 'cikis':
        navigate('/accounting/expenses');
        break;
      case 'takip':
      case 'alacak':
      case 'borc':
        navigate('/accounting/takipler');
        break;
      case 'cek':
        navigate('/accounting/checks');
        break;
      default:
        break;
    }
    
    setDetailModalOpen(false);
  };

  // Onaylanan iş için işlem modalı aç
  const openJobActionModal = (job, actionType) => {
    // State'leri sıfırla
    setEditingTransaction(null);
    
    // Form verilerini sıfırla
    const now = new Date();
    setJobActionData({
      tarih: now.toISOString().split('T')[0],
      tutar: '',
      aciklama: '',
      cari_hesap_id: '',
      cari_hesap_adi: '',
      hesap_id: '',
      hesap_adi: '',
      vade_tarihi: '',
      belgeler: []
    });
    
    // Pending transactions yükle
    loadPendingTransactions(job.id);
    
    // Modal'ı aç - tek state güncellemesi ile hem type hem open ayarla
    setJobActionModal({ open: true, type: actionType });
  };

  // İşe ait bekleyen işlemleri yükle
  const loadPendingTransactions = async (jobId) => {
    try {
      const response = await fetch(`${API}/accounting/isler/${jobId}/pending-transactions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPendingTransactions(data || []);
      }
    } catch (error) {
      console.error('Bekleyen işlemler yüklenemedi:', error);
    }
  };

  // Bekleyen işlem ekle (sadece geçici listeye, DB'ye değil)
  const addPendingTransaction = async () => {
    if (!selectedJob || !jobActionModal.type) return;
    
    if (!jobActionData.tutar || parseFloat(jobActionData.tutar) <= 0) {
      toast.error('Lütfen geçerli bir tutar girin');
      return;
    }
    
    setJobActionSubmitting(true);
    try {
      const payload = {
        type: jobActionModal.type,
        tarih: jobActionData.tarih,
        tutar: parseFloat(jobActionData.tutar),
        aciklama: jobActionData.aciklama || '',
        vade_tarihi: jobActionData.vade_tarihi || null,
        cari_hesap_id: jobActionData.cari_hesap_id || null,
        cari_hesap_adi: jobActionData.cari_hesap_adi || '',
        hesap_id: jobActionData.hesap_id || null,
        hesap_adi: jobActionData.hesap_adi || '',
        hesap_tipi: jobActionData.hesap_tipi || null,
        belgeler: jobActionData.belgeler || []
      };
      
      const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/pending-transactions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('İşlem listeye eklendi');
        await loadPendingTransactions(selectedJob.id);
        // Formu sıfırla ve modalı kapat
        setJobActionData({
          tarih: new Date().toISOString().split('T')[0],
          tutar: '',
          aciklama: '',
          cari_hesap_id: '',
          cari_hesap_adi: '',
          hesap_id: '',
          hesap_adi: '',
          vade_tarihi: '',
          belgeler: []
        });
        setJobActionModal({ open: false, type: null });
      } else {
        const err = await response.json();
        toast.error(err.detail || 'İşlem eklenemedi');
      }
    } catch (error) {
      console.error('İşlem ekleme hatası:', error);
      toast.error('İşlem eklenemedi');
    }
    setJobActionSubmitting(false);
  };

  // Bekleyen işlemi sil
  const deletePendingTransaction = async (txId) => {
    if (!selectedJob) return;
    
    try {
      const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/pending-transactions/${txId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('İşlem silindi');
        await loadPendingTransactions(selectedJob.id);
      } else {
        toast.error('İşlem silinemedi');
      }
    } catch (error) {
      console.error('İşlem silme hatası:', error);
      toast.error('İşlem silinemedi');
    }
  };

  // İşlem düzenleme modalını aç
  const openEditTransactionModal = (tx) => {
    setEditingTransaction(tx);
    setJobActionData({
      tarih: tx.tarih || '',
      tutar: tx.tutar || '',
      aciklama: tx.aciklama || '',
      cari_hesap_id: tx.cari_hesap_id || '',
      cari_hesap_adi: tx.cari_hesap_adi || '',
      hesap_id: tx.hesap_id || '',
      hesap_adi: tx.hesap_adi || '',
      vade_tarihi: tx.vade_tarihi || '',
      belgeler: tx.belgeler || []
    });
    setJobActionModal({ open: true, type: tx.type });
  };

  // Bekleyen işlemi güncelle
  const updatePendingTransaction = async () => {
    if (!selectedJob || !editingTransaction) return;
    
    if (!jobActionData.tutar || parseFloat(jobActionData.tutar) <= 0) {
      toast.error('Lütfen geçerli bir tutar girin');
      return;
    }
    
    setJobActionSubmitting(true);
    try {
      const payload = {
        type: jobActionModal.type,
        tarih: jobActionData.tarih,
        tutar: parseFloat(jobActionData.tutar),
        aciklama: jobActionData.aciklama || '',
        vade_tarihi: jobActionData.vade_tarihi || null,
        cari_hesap_id: jobActionData.cari_hesap_id || null,
        cari_hesap_adi: jobActionData.cari_hesap_adi || '',
        hesap_id: jobActionData.hesap_id || null,
        hesap_adi: jobActionData.hesap_adi || ''
      };
      
      const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/pending-transactions/${editingTransaction.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('İşlem güncellendi');
        await loadPendingTransactions(selectedJob.id);
        setEditingTransaction(null);
        setJobActionModal({ open: false, type: null });
      } else {
        toast.error('İşlem güncellenemedi');
      }
    } catch (error) {
      console.error('İşlem güncelleme hatası:', error);
      toast.error('İşlem güncellenemedi');
    }
    setJobActionSubmitting(false);
  };

  // İşi tamamla (approved -> active/hazırlanan) - Tüm bekleyen işlemleri DB'ye kaydet
  const completeApprovedJob = async () => {
    if (!selectedJob) return;
    
    if (pendingTransactions.length === 0) {
      toast.error('Hazırlamak için en az bir işlem eklemeniz gerekiyor');
      return;
    }
    
    try {
      const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/complete-approved`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          note: completeJobNote
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'İş hazırlandı ve Yönetim Kurulu onayına sunuldu');
        setCompleteJobModalOpen(false);
        setDetailModalOpen(false);
        setCompleteJobNote('');
        setPendingTransactions([]);
        loadJobs();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'İş hazırlanamadı');
      }
    } catch (error) {
      console.error('İş tamamlama hatası:', error);
      toast.error('İş tamamlanamadı');
    }
  };

  // İşi tamamla (backend'e bildir)
  const completeJob = async (jobId, completedAction, relatedRecordId) => {
    try {
      const response = await fetch(`${API}/accounting/isler/${jobId}/complete`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          completed_action: completedAction,
          related_record_id: relatedRecordId
        })
      });
      
      if (response.ok) {
        toast.success('İş tamamlandı');
        loadJobs();
        loadCompletedJobs();
      } else {
        toast.error('İş tamamlanamadı');
      }
    } catch (error) {
      console.error('İş tamamlama hatası:', error);
      toast.error('İş tamamlanamadı');
    }
  };

  // İşi hazırlamaya başla (todo -> active)
  const startJob = async (jobId) => {
    try {
      const response = await fetch(`${API}/accounting/isler/${jobId}/start`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('İş hazırlanmaya başlandı');
        loadJobs();
      } else {
        toast.error('İş başlatılamadı');
      }
    } catch (error) {
      console.error('İş başlatma hatası:', error);
      toast.error('İş başlatılamadı');
    }
  };

  // Yapılacak işe belge ekle
  const handleAddTodoDocuments = async () => {
    if (!selectedJob || todoDocuments.length === 0) {
      toast.error('En az bir belge seçmelisiniz');
      return;
    }
    
    setTodoDocumentsSubmitting(true);
    try {
      const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/add-documents`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ belgeler: todoDocuments })
      });
      
      if (response.ok) {
        toast.success('Belgeler eklendi');
        setAddDocumentsModalOpen(false);
        setTodoDocuments([]);
        // selectedJob'ı güncelle
        setSelectedJob(prev => ({
          ...prev,
          belgeler: [...(prev.belgeler || []), ...todoDocuments]
        }));
        loadJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Belgeler eklenemedi');
      }
    } catch (error) {
      console.error('Belge ekleme hatası:', error);
      toast.error('Belgeler eklenemedi');
    }
    setTodoDocumentsSubmitting(false);
  };

  // Yapılacak iş belge dosyası yükleme
  const handleTodoDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newBelgeler = [];
    
    for (const file of files) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newBelgeler.push({
        name: file.name,
        size: file.size,
        type: file.type,
        url: base64
      });
    }
    
    setTodoDocuments(prev => [...prev, ...newBelgeler]);
  };

  // Yapılacak işi tamamla
  const handleCompleteTodoJob = async () => {
    if (!selectedJob) return;
    
    try {
      const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/complete-todo`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ note: completeJobNote })
      });
      
      if (response.ok) {
        toast.success('İş tamamlandı');
        setDetailModalOpen(false);
        setCompleteJobNote('');
        loadJobs();
        loadCompletedJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İş tamamlanamadı');
      }
    } catch (error) {
      console.error('İş tamamlama hatası:', error);
      toast.error('İş tamamlanamadı');
    }
  };

  // İşleri okundu olarak işaretle
  const markJobsAsRead = useCallback(async (jobs) => {
    if (!jobs || jobs.length === 0) return;
    const jobIds = jobs.map(job => job.id);
    try {
      await fetch(`${API}/accounting/isler/mark-read-bulk`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ job_ids: jobIds })
      });
    } catch (error) {
      console.error('İşleri okundu işaretlerken hata:', error);
    }
  }, []);

  // Tab değiştiğinde işleri okundu olarak işaretle
  useEffect(() => {
    const markCurrentTabAsRead = async () => {
      if (activeTab === 'active' && jobs.length > 0) {
        await markJobsAsRead(jobs);
      } else if (activeTab === 'correction' && correctionJobs.length > 0) {
        await markJobsAsRead(correctionJobs);
      } else if (activeTab === 'cancelled' && cancelledJobs.length > 0) {
        await markJobsAsRead(cancelledJobs);
      }
    };
    
    // Küçük bir gecikme ile çağır (veriler yüklendikten sonra)
    const timer = setTimeout(markCurrentTabAsRead, 500);
    return () => clearTimeout(timer);
  }, [activeTab, jobs, correctionJobs, cancelledJobs, markJobsAsRead]);

  // İlk yüklemede tüm verileri çek (stats için)
  useEffect(() => {
    loadJobs();
    loadPendingJobs();
    loadCorrectionJobs();
    loadCancelledJobs();
    loadCompletedJobs();
    loadManagerRequests();
    loadIncomingRequests();
    loadMyRequests();
  }, [loadJobs, loadPendingJobs, loadCorrectionJobs, loadCancelledJobs, loadCompletedJobs, loadManagerRequests, loadIncomingRequests, loadMyRequests]);

  // Sekme değiştiğinde ilgili verileri yenile
  useEffect(() => {
    if (activeTab === 'archive') {
      loadCancelledJobs();
      loadCompletedJobs();
    } else if (activeTab === 'pending') {
      loadPendingJobs();
    } else if (activeTab === 'correction') {
      loadCorrectionJobs();
    } else if (activeTab === 'cancelled') {
      loadCancelledJobs();
    } else if (activeTab === 'completed') {
      loadCompletedJobs();
    } else if (activeTab === 'manager_requests') {
      loadManagerRequests();
    } else if (activeTab === 'talepler') {
      loadIncomingRequests();
      loadMyRequests();
    } else {
      loadJobs();
    }
  }, [activeTab, loadJobs, loadPendingJobs, loadCorrectionJobs, loadCancelledJobs, loadArchivedJobs, loadCompletedJobs, loadManagerRequests, loadIncomingRequests, loadMyRequests]);

  // Sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = (data) => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  };

  // Filter
  const filteredJobs = useMemo(() => {
    let result;
    if (activeTab === 'archive') {
      result = archivedJobs;
    } else if (activeTab === 'pending') {
      result = pendingJobs;
    } else if (activeTab === 'correction') {
      result = correctionJobs;
    } else if (activeTab === 'cancelled') {
      result = cancelledJobs;
    } else {
      result = jobs;
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(job => 
        job.konu?.toLowerCase().includes(term) ||
        job.aciklama?.toLowerCase().includes(term) ||
        job.sube_adi?.toLowerCase().includes(term)
      );
    }
    
    return sortData(result);
  }, [jobs, pendingJobs, correctionJobs, cancelledJobs, archivedJobs, activeTab, searchTerm, sortConfig]);

  // Helper functions
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Aktif', className: 'bg-green-100 text-green-700 border-green-300' },
      pending: { label: 'Beklemede', className: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      needs_correction: { label: 'Düzeltilmeli', className: 'bg-orange-100 text-orange-700 border-orange-300' },
      completed: { label: 'Tamamlandı', className: 'bg-blue-100 text-blue-700 border-blue-300' },
      cancelled: { label: 'İptal', className: 'bg-red-100 text-red-700 border-red-300' },
      archived: { label: 'Arşiv', className: 'bg-gray-100 text-gray-700 border-gray-300' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const dateFormatted = date.toLocaleDateString('tr-TR');
    const timeFormatted = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return (
      <div>
        <div>{dateFormatted}</div>
        <div className="text-xs text-gray-500">{timeFormatted}</div>
      </div>
    );
  };

  const formatDateTimeText = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const dateFormatted = date.toLocaleDateString('tr-TR');
    const timeFormatted = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return `${dateFormatted} ${timeFormatted}`;
  };

  const SortableHeader = ({ field, children }) => (
    <TableHead 
      className="cursor-pointer hover:bg-gray-50 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortConfig.key === field ? (
          sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
        ) : (
          <ArrowUpDown className="h-4 w-4 opacity-30" />
        )}
      </div>
    </TableHead>
  );

  // File upload handler
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newBelgeler = [];
    
    for (const file of files) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newBelgeler.push({
        name: file.name,
        size: file.size,
        type: file.type,
        url: base64
      });
    }
    
    setFormData(prev => ({
      ...prev,
      belgeler: [...prev.belgeler, ...newBelgeler]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  // Submit new job
  const handleSubmit = async () => {
    if (!formData.konu.trim()) {
      toast.error('İşin konusu zorunludur');
      return;
    }
    // Şube seçimi opsiyonel - seçilmezse backend kullanıcının şubesini kullanır
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/accounting/isler`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          konu: formData.konu,
          aciklama: formData.aciklama,
          belgeler: formData.belgeler,
          sube_id: formData.sube_id || null  // Boşsa null gönder, backend kullanıcının şubesini kullanır
        })
      });
      
      if (response.ok) {
        toast.success('İş başarıyla oluşturuldu - Onay bekliyor');
        setAddModalOpen(false);
        setFormData({ konu: '', aciklama: '', belgeler: [], sube_id: '' });
        setActiveTab('pending');  // Onay Bekleyen sekmesine geç
        loadPendingJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İş oluşturulamadı');
      }
    } catch (error) {
      console.error('İş oluşturma hatası:', error);
      toast.error('İş oluşturulamadı');
    }
    setSubmitting(false);
  };

  // Onayla ve İşe Çevir
  const handleApproveAndConvertToJob = () => {
    if (!selectedTalep) return;
    
    // Talebin bilgilerini form'a doldur
    setFormData({
      konu: selectedTalep.title || '',
      aciklama: selectedTalep.description || '',
      belgeler: selectedTalep.documents || [],
      sube_id: selectedTalep.branch_id || ''
    });
    
    // İşe çevir modalını aç
    setConvertToJobModalOpen(true);
  };

  // Onayla ve İşe Çevir Submit
  const handleSubmitConvertToJob = async () => {
    if (!formData.konu.trim()) {
      toast.error('İşin konusu zorunludur');
      return;
    }
    if (!selectedTalep) return;
    
    setSubmitting(true);
    try {
      // 1. İşi oluştur (source_request bilgisi ile)
      const jobResponse = await fetch(`${API}/accounting/isler`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          konu: formData.konu,
          aciklama: formData.aciklama,
          belgeler: formData.belgeler,
          sube_id: formData.sube_id || null,
          source_request_id: selectedTalep.id,
          source_request: {
            id: selectedTalep.id,
            request_number: selectedTalep.request_number,
            title: selectedTalep.title,
            category: selectedTalep.category,
            owner_name: selectedTalep.owner_name
          }
        })
      });
      
      if (!jobResponse.ok) {
        const error = await jobResponse.json();
        toast.error(error.detail || 'İş oluşturulamadı');
        setSubmitting(false);
        return;
      }
      
      // 2. Talebi "converted_to_job" olarak işaretle
      await fetch(`${API}/work/requests/${selectedTalep.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status: 'converted_to_job',
          response_note: 'İşe dönüştürüldü'
        })
      });
      
      toast.success('Talep onaylandı ve iş olarak eklendi');
      setConvertToJobModalOpen(false);
      setSelectedTalep(null);
      setFormData({ konu: '', aciklama: '', belgeler: [], sube_id: '' });
      loadIncomingRequests();
      loadPendingJobs();
      setActiveTab('active');
      setJobSubTab('pending');
    } catch (error) {
      console.error('İşe dönüştürme hatası:', error);
      toast.error('İşe dönüştürme başarısız');
    }
    setSubmitting(false);
  };

  // Delete job
  const handleDelete = async (jobId) => {
    if (!window.confirm('Bu işi silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API}/accounting/isler/${jobId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('İş silindi');
        loadJobs();
      } else {
        toast.error('İş silinemedi');
      }
    } catch (error) {
      console.error('İş silme hatası:', error);
      toast.error('İş silinemedi');
    }
  };

  // Open correction modal
  const openCorrectionModal = (job) => {
    setSelectedJob(job);
    setCorrectionFormData({
      konu: job.konu || '',
      aciklama: '',
      belgeler: []
    });
    setDetailModalOpen(false);
    setCorrectionModalOpen(true);
  };

  // File upload for correction
  const handleCorrectionFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newBelgeler = [];
    
    for (const file of files) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newBelgeler.push({
        name: file.name,
        size: file.size,
        type: file.type,
        url: base64
      });
    }
    
    setCorrectionFormData(prev => ({
      ...prev,
      belgeler: [...prev.belgeler, ...newBelgeler]
    }));
  };

  const removeCorrectionFile = (index) => {
    setCorrectionFormData(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  // Submit correction
  const handleSubmitCorrection = async () => {
    if (!correctionFormData.konu.trim()) {
      toast.error('İşin konusu zorunludur');
      return;
    }
    
    setCorrectionSubmitting(true);
    try {
      const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/submit-correction`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          konu: correctionFormData.konu,
          aciklama: correctionFormData.aciklama,
          belgeler: correctionFormData.belgeler
        })
      });
      
      if (response.ok) {
        toast.success('Düzeltme gönderildi - İş tekrar onay bekleyenlere eklendi');
        setCorrectionModalOpen(false);
        setCorrectionFormData({ konu: '', aciklama: '', belgeler: [] });
        setSelectedJob(null);
        setActiveTab('pending');
        loadPendingJobs();
        loadCorrectionJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Düzeltme gönderilemedi');
      }
    } catch (error) {
      console.error('Düzeltme gönderme hatası:', error);
      toast.error('Düzeltme gönderilemedi');
    }
    setCorrectionSubmitting(false);
  };

  // Stats
  const stats = useMemo(() => {
    const activeCount = jobs.length;  // jobs artık sadece active olanları içeriyor
    const pendingCount = pendingJobs.length;
    const correctionCount = correctionJobs.length;
    const cancelledCount = cancelledJobs.length;
    const totalJobs = activeCount + pendingCount + correctionCount + cancelledCount;
    
    return { activeCount, pendingCount, correctionCount, cancelledCount, totalJobs };
  }, [jobs, pendingJobs, correctionJobs, cancelledJobs]);

  // Render table rows - showDelete: sadece Onay Bekleyen tabında true olacak, tabType: sayaç için tab tipi
  const renderTableRows = (data, bgColor = 'hover:bg-gray-50', showDelete = false, tabType = 'active') => {
    if (data.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} className="text-center py-8 text-gray-500">
            İş bulunamadı
          </TableCell>
        </TableRow>
      );
    }
    
    return data.map((job) => (
      <TableRow key={job.id} className={bgColor}>
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            {formatDateShort(job.created_at)}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-gray-400" />
            <span className="font-medium">{job.konu || '-'}</span>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-gray-400" />
            {job.sube_adi || '-'}
          </div>
        </TableCell>
        <TableCell>
          {job.talep_eden ? (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-purple-400" />
              <span className="text-purple-700 font-medium">{job.talep_eden}</span>
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </TableCell>
        <TableCell>
          <span className="text-gray-600 line-clamp-2">{job.aciklama || '-'}</span>
        </TableCell>
        <TableCell>
          {job.belgeler && job.belgeler.length > 0 ? (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <FileText className="h-3 w-3 mr-1" />
              {job.belgeler.length} Belge
            </Badge>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedJob(job);
                setDetailModalOpen(true);
                markAsViewed(tabType, job.id);
                // Bekleyen işlemleri yükle (approved ve prepared durumları için)
                if (job.status === 'approved' || job.status === 'prepared' || job.status === 'todo' || job.status === 'new') {
                  loadPendingTransactions(job.id);
                }
              }}
              data-testid={`view-job-${job.id}`}
            >
              <Eye className="h-4 w-4" />
            </Button>
            {/* Yapılacak İşler için Hazırlamaya Başla butonu */}
            {(job.status === 'todo' || job.status === 'new') && (
              <Button
                variant="ghost"
                size="sm"
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                onClick={() => startJob(job.id)}
                data-testid={`start-job-${job.id}`}
                title="Hazırlamaya Başla"
              >
                <PlayCircle className="h-4 w-4" />
              </Button>
            )}
            {showDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleDelete(job.id)}
                data-testid={`delete-job-${job.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-jobs">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">İşler</h1>
          <p className="text-gray-500">Muhasebe işlerini yönetin</p>
        </div>
        <Button 
          onClick={() => setAddModalOpen(true)}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="add-job-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          İş Ekle
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Toplam İş</p>
                <p className="text-2xl font-bold">{stats.totalJobs}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Aktif</p>
                <p className="text-2xl font-bold">{stats.activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="pending-jobs-stat">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Onay Bekleyen</p>
                <p className="text-2xl font-bold">{stats.pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Düzeltilmeli</p>
                <p className="text-2xl font-bold">{stats.correctionCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Ban className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">İptal Edilen</p>
                <p className="text-2xl font-bold">{stats.cancelledCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="İş ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-jobs-input"
          />
        </div>
      </div>

      {/* Tabs - 4 Ana Sekme */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="active" className="gap-2" data-testid="tab-active">
            <Briefcase className="h-4 w-4" />
            AKTİF İŞLER
          </TabsTrigger>
          <TabsTrigger value="services" className="gap-2" data-testid="tab-services">
            <Wrench className="h-4 w-4" />
            ALINAN HİZMETLER
          </TabsTrigger>
          <TabsTrigger value="subcontractors" className="gap-2" data-testid="tab-subcontractors">
            <HardHat className="h-4 w-4" />
            TAŞERONLAR
          </TabsTrigger>
          <TabsTrigger value="archive" className="gap-2" data-testid="tab-archive">
            <Archive className="h-4 w-4" />
            ARŞİV
          </TabsTrigger>
        </TabsList>

        {/* AKTİF İŞLER Tab */}
        <TabsContent value="active" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-emerald-600" />
                Aktif İşler
              </CardTitle>
              <div className="flex gap-2">
                <Button onClick={() => setAddModalOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-job-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  İş Ekle
                </Button>
                <Button onClick={() => setCorrectionModalOpen(true)} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Düzeltme Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Alt Durum Filtreleri */}
              <div className="flex gap-2 mb-4 border-b flex-wrap">
                <button
                  onClick={() => setJobSubTab('pending')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'pending' 
                      ? 'border-yellow-600 text-yellow-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Onay Bekleyen
                  <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-700">
                    {pendingJobs.length}
                  </Badge>
                </button>
                <button
                  onClick={() => setJobSubTab('approved')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'approved' 
                      ? 'border-green-600 text-green-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Onaylanan İşler
                  <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">
                    {jobs.filter(j => j.status === 'approved').length}
                  </Badge>
                </button>
                <button
                  onClick={() => setJobSubTab('active')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'active' 
                      ? 'border-emerald-600 text-emerald-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Hazırlanan İşler
                  <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">
                    {jobs.filter(j => j.status !== 'todo' && j.status !== 'new' && j.status !== 'approved').length}
                  </Badge>
                </button>
                <button
                  onClick={() => setJobSubTab('todo')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'todo' 
                      ? 'border-purple-600 text-purple-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Yapılacak İşler
                  <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-700">
                    {jobs.filter(j => j.status === 'todo' || j.status === 'new').length}
                  </Badge>
                </button>
                <button
                  onClick={() => setJobSubTab('completed')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'completed' 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Tamamlanan
                  <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">
                    {completedJobs.length}
                  </Badge>
                </button>
                <button
                  onClick={() => setJobSubTab('correction')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'correction' 
                      ? 'border-orange-600 text-orange-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Düzeltme
                  <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-700">
                    {correctionJobs.length}
                  </Badge>
                </button>
                <button
                  onClick={() => setJobSubTab('cancelled')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'cancelled' 
                      ? 'border-red-600 text-red-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  İptal Edilen
                  <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700">
                    {cancelledJobs.length}
                  </Badge>
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                </div>
              ) : getFilteredJobsBySubTab().length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Bu kategoride iş yok</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-emerald-50">
                        <SortableHeader field="created_at">Tarih</SortableHeader>
                        <SortableHeader field="konu">Konu</SortableHeader>
                        <TableHead>Açıklama</TableHead>
                        <SortableHeader field="sube_adi">Şube</SortableHeader>
                        <TableHead>İşlemler / Kayıtlar</TableHead>
                        <TableHead>Onay Bilgileri</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead className="text-right">Detay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getFilteredJobsBySubTab().map((job) => {
                        const statusConfig = {
                          active: { label: 'Devam Ediyor', color: 'bg-emerald-100 text-emerald-700' },
                          pending: { label: 'Onay Bekliyor', color: 'bg-yellow-100 text-yellow-700' },
                          todo: { label: 'Yapılacak', color: 'bg-purple-100 text-purple-700' },
                          new: { label: 'Yeni', color: 'bg-indigo-100 text-indigo-700' },
                          prepared: { label: 'Hazırlandı', color: 'bg-cyan-100 text-cyan-700' },
                          approved: { label: 'Onaylandı', color: 'bg-green-100 text-green-700' },
                          correction: { label: 'Düzeltme', color: 'bg-orange-100 text-orange-700' },
                          needs_correction: { label: 'Düzeltme Gerekli', color: 'bg-orange-100 text-orange-700' },
                          completed: { label: 'Tamamlandı', color: 'bg-blue-100 text-blue-700' },
                          cancelled: { label: 'İptal Edildi', color: 'bg-red-100 text-red-700' }
                        };
                        const status = statusConfig[job.status] || { label: job.status, color: 'bg-gray-100 text-gray-700' };
                        
                        // İşlemler ve kayıtları hesapla
                        const transactions = job.transactions || [];
                        const createdRecords = job.created_records || [];
                        const txTutar = (t) => parseFloat(t.tutar) || parseFloat(t.form_data?.miktar) || parseFloat(t.form_data?.tutar) || parseFloat(t.form_data?.toplam_tutar) || 0;
                        const totalTutar = transactions.reduce((sum, t) => sum + txTutar(t), 0);
                        
                        return (
                          <TableRow key={job.id} className="hover:bg-emerald-50/50">
                            {/* Tarih */}
                            <TableCell className="text-sm whitespace-nowrap">
                              <div>
                                <div className="font-medium">{new Date(job.created_at).toLocaleDateString('tr-TR')}</div>
                                <div className="text-xs text-gray-500">{new Date(job.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                              </div>
                            </TableCell>
                            
                            {/* Konu */}
                            <TableCell className="font-medium max-w-[150px]">
                              <div className="truncate" title={job.konu || job.name || '-'}>
                                {job.konu || job.name || '-'}
                              </div>
                              {job.created_by_name && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Oluşturan: {job.created_by_name}
                                </div>
                              )}
                            </TableCell>
                            
                            {/* Açıklama */}
                            <TableCell className="max-w-[200px]">
                              <div className="text-sm text-gray-700 line-clamp-2" title={job.aciklama || '-'}>
                                {job.aciklama || '-'}
                              </div>
                            </TableCell>
                            
                            {/* Şube */}
                            <TableCell className="whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                <Building2 className="h-3 w-3 text-gray-400" />
                                <span className="text-sm">{job.sube_adi || '-'}</span>
                              </div>
                            </TableCell>
                            
                            {/* İşlemler / Kayıtlar */}
                            <TableCell className="min-w-[180px]">
                              {transactions.length > 0 ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1 text-sm">
                                    <ListChecks className="h-3 w-3 text-blue-500" />
                                    <span className="font-medium">{transactions.length} işlem</span>
                                    <span className="text-gray-500">
                                      ({totalTutar.toLocaleString('tr-TR')} ₺)
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {transactions.slice(0, 3).map((t, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs py-0 px-1">
                                        {t.type === 'giris' ? 'Giriş' : t.type === 'cikis' ? 'Çıkış' : t.type === 'cek' ? 'Çek' : t.type === 'alacak' ? 'Alacak' : t.type === 'borc' ? 'Borç' : t.type}
                                        : {txTutar(t).toLocaleString('tr-TR')} ₺
                                      </Badge>
                                    ))}
                                    {transactions.length > 3 && (
                                      <Badge variant="outline" className="text-xs py-0 px-1 bg-gray-50">
                                        +{transactions.length - 3} daha
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ) : createdRecords.length > 0 ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1 text-sm text-green-700">
                                    <CheckCircle className="h-3 w-3" />
                                    <span className="font-medium">{createdRecords.length} kayıt oluşturuldu</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {createdRecords.map((r, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs py-0 px-1 bg-green-50 text-green-700 border-green-200">
                                        {r.seri_no || r.type}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </TableCell>
                            
                            {/* Onay Bilgileri */}
                            <TableCell className="min-w-[150px]">
                              <div className="space-y-1 text-xs">
                                {/* Çift Onay Sistemi - pending durumunda göster */}
                                {job.status === 'pending' && (
                                  <>
                                    <div className="flex items-center gap-1">
                                      {job.accounting_approved ? (
                                        <Badge className="bg-green-100 text-green-700 text-xs py-0 px-1">
                                          <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                                          Muhasebe ✓
                                        </Badge>
                                      ) : (
                                        <Badge className="bg-yellow-100 text-yellow-700 text-xs py-0 px-1">
                                          Muhasebe Bekliyor
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {job.management_approved ? (
                                        <Badge className="bg-green-100 text-green-700 text-xs py-0 px-1">
                                          <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                                          Yönetim ✓
                                        </Badge>
                                      ) : (
                                        <Badge className="bg-gray-100 text-gray-500 text-xs py-0 px-1">
                                          Yönetim Bekliyor
                                        </Badge>
                                      )}
                                    </div>
                                  </>
                                )}
                                {/* Diğer durumlar için eski görünüm */}
                                {job.status !== 'pending' && (
                                  <>
                                    {job.approved_by_name && (
                                      <div className="flex items-center gap-1 text-green-700">
                                        <CheckCircle className="h-3 w-3" />
                                        <span>Onay: {job.approved_by_name}</span>
                                      </div>
                                    )}
                                    {job.prepared_by_name && (
                                      <div className="flex items-center gap-1 text-cyan-700">
                                        <ClipboardCheck className="h-3 w-3" />
                                        <span>Hazırlayan: {job.prepared_by_name}</span>
                                      </div>
                                    )}
                                    {job.board_approved_by_name && (
                                      <div className="flex items-center gap-1 text-indigo-700">
                                        <CheckCircle className="h-3 w-3" />
                                        <span>YK Onay: {job.board_approved_by_name}</span>
                                      </div>
                                    )}
                                    {!job.approved_by_name && !job.prepared_by_name && !job.board_approved_by_name && (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </>
                                )}
                              </div>
                            </TableCell>
                            
                            {/* Durum */}
                            <TableCell>
                              <Badge className={status.color}>{status.label}</Badge>
                              {job.belgeler?.length > 0 && (
                                <div className="mt-1">
                                  <Badge variant="outline" className="text-xs py-0">
                                    <FileText className="h-3 w-3 mr-1" />
                                    {job.belgeler.length}
                                  </Badge>
                                </div>
                              )}
                            </TableCell>
                            
                            {/* Detay Butonu */}
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { 
                                  setSelectedJob(job); 
                                  setDetailModalOpen(true);
                                  if (job.status === 'approved' || job.status === 'prepared') {
                                    loadPendingTransactions(job.id);
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                            
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alınan Hizmetler Tab */}
        <TabsContent value="services" className="mt-6">
          <PurchasingServices viewMode="muhasebe" />
        </TabsContent>

        {/* Taşeronlar Tab */}
        <TabsContent value="subcontractors" className="mt-6">
          <PurchasingSubcontractors viewMode="muhasebe" />
        </TabsContent>

        {/* Archive Tab */}
        <TabsContent value="archive" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-600">
                <Archive className="h-5 w-5" />
                Arşiv
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={archiveSubTab} onValueChange={setArchiveSubTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="cancelled" className="gap-2" data-testid="archive-tab-cancelled">
                    <XCircle className="h-4 w-4" />
                    İptal Edilenler
                    {cancelledJobs.length > 0 && (
                      <Badge className="ml-1 bg-red-100 text-red-700 border-0">{cancelledJobs.length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="completed" className="gap-2" data-testid="archive-tab-completed">
                    <CheckCircle className="h-4 w-4" />
                    Tamamlananlar
                    {completedJobs.length > 0 && (
                      <Badge className="ml-1 bg-green-100 text-green-700 border-0">{completedJobs.length}</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* İptal Edilenler */}
                <TabsContent value="cancelled">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
                    </div>
                  ) : cancelledJobs.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <XCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium">İptal edilmiş iş yok</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-red-50">
                            <TableHead>Oluşturulma Tarihi</TableHead>
                            <TableHead>İşin Konusu</TableHead>
                            <TableHead>Şube</TableHead>
                            <TableHead>Talep Eden</TableHead>
                            <TableHead>Belgeler</TableHead>
                            <TableHead className="text-right">Detay</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {cancelledJobs.map((job) => (
                            <TableRow key={job.id} className="hover:bg-red-50/50">
                              <TableCell className="text-sm">
                                <div>
                                  <div>{new Date(job.created_at).toLocaleDateString('tr-TR')}</div>
                                  <div className="text-xs text-gray-500">{new Date(job.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">{job.konu || job.name || '-'}</TableCell>
                              <TableCell>{job.sube_adi || '-'}</TableCell>
                              <TableCell>{job.talep_eden || '-'}</TableCell>
                              <TableCell>
                                {job.belgeler?.length > 0 && (
                                  <Badge variant="outline">{job.belgeler.length} belge</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setSelectedJob(job); setDetailModalOpen(true); if (job.pending_transactions?.length > 0) loadPendingTransactions(job.id); }}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>

                {/* Tamamlananlar */}
                <TabsContent value="completed">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
                    </div>
                  ) : completedJobs.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium">Tamamlanmış iş yok</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-green-50">
                            <TableHead>Oluşturulma Tarihi</TableHead>
                            <TableHead>İşin Konusu</TableHead>
                            <TableHead>Şube</TableHead>
                            <TableHead>Talep Eden</TableHead>
                            <TableHead>Belgeler</TableHead>
                            <TableHead className="text-right">Detay</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {completedJobs.map((job) => (
                            <TableRow key={job.id} className="hover:bg-green-50/50">
                              <TableCell className="text-sm">
                                <div>
                                  <div>{new Date(job.created_at).toLocaleDateString('tr-TR')}</div>
                                  <div className="text-xs text-gray-500">{new Date(job.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">{job.konu || job.name || '-'}</TableCell>
                              <TableCell>{job.sube_adi || '-'}</TableCell>
                              <TableCell>{job.talep_eden || '-'}</TableCell>
                              <TableCell>
                                {job.belgeler?.length > 0 && (
                                  <Badge variant="outline">{job.belgeler.length} belge</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setSelectedJob(job); setDetailModalOpen(true); if (job.pending_transactions?.length > 0) loadPendingTransactions(job.id); }}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Talep Detay Modal */}
      <Dialog open={!!selectedTalep} onOpenChange={() => { setSelectedTalep(null); setTalepActionNote(''); }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <Eye className="h-5 w-5" />
              Talep Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedTalep && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {selectedTalep.request_number && (
                <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-indigo-600" />
                    <span className="text-sm text-indigo-700 font-medium">Talep No:</span>
                    <span className="text-sm text-indigo-900 font-mono">{selectedTalep.request_number}</span>
                  </div>
                </div>
              )}
              
              {selectedTalep.owner_name && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-600" />
                    <span className="text-sm text-gray-700 font-medium">Gönderen:</span>
                    <span className="text-sm text-gray-900">{selectedTalep.owner_name}</span>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Başlık</Label>
                  <p className="font-medium">{selectedTalep.title}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Kategori</Label>
                  <p className="font-medium">{selectedTalep.category}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Tarih</Label>
                  <p className="font-medium">
                    {new Date(selectedTalep.created_at).toLocaleDateString('tr-TR')}
                    <span className="text-gray-500 text-sm ml-2">
                      {new Date(selectedTalep.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Durum</Label>
                  <Badge className={
                    selectedTalep.status === 'approved' ? 'bg-green-100 text-green-700' :
                    selectedTalep.status?.includes('rejected') ? 'bg-red-100 text-red-700' :
                    selectedTalep.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }>
                    {selectedTalep.status === 'approved' ? 'Onaylandı' :
                     selectedTalep.status?.includes('rejected') ? 'Reddedildi' :
                     selectedTalep.status === 'completed' ? 'Bitirildi' :
                     'Bekliyor'}
                  </Badge>
                </div>
              </div>
              
              {selectedTalep.description && (
                <div>
                  <Label className="text-gray-500 text-xs">Açıklama</Label>
                  <p className="text-sm mt-1 p-2 bg-gray-50 rounded">{selectedTalep.description}</p>
                </div>
              )}
              
              {selectedTalep.documents && selectedTalep.documents.length > 0 && (
                <div>
                  <Label className="text-gray-500 text-xs">Belgeler ({selectedTalep.documents.length})</Label>
                  <div className="space-y-2 mt-2">
                    {selectedTalep.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{doc.name}</span>
                        </div>
                        {(doc.url || doc.data) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const fileSrc = doc.url || doc.data;
                              if (fileSrc.startsWith('data:')) {
                                const link = document.createElement('a');
                                link.href = fileSrc;
                                link.download = doc.name;
                                link.click();
                              } else {
                                window.open(fileSrc, '_blank');
                              }
                            }}
                            className="text-blue-600"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* İşlem butonları (sadece bekleyen talepler için) */}
              {(selectedTalep.status === 'forwarded_to_accounting' || selectedTalep.status === 'pending_accounting') && (
                <div className="border-t pt-4 mt-4 space-y-4">
                  <Label className="text-gray-700 font-medium">İşlem Yap</Label>
                  
                  <div>
                    <Label className="text-gray-500 text-xs">Açıklama / Not</Label>
                    <Textarea
                      value={talepActionNote}
                      onChange={(e) => setTalepActionNote(e.target.value)}
                      placeholder="İşlem ile ilgili notunuzu yazın..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => handleTalepAction('approve')}
                      disabled={talepActionLoading}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="talep-approve-btn"
                    >
                      {talepActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Onayla
                    </Button>
                    <Button
                      onClick={handleApproveAndConvertToJob}
                      disabled={talepActionLoading}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="talep-approve-convert-btn"
                    >
                      <Briefcase className="h-4 w-4 mr-2" />
                      Onayla ve İşe Çevir
                    </Button>
                    <Button
                      onClick={() => handleTalepAction('complete')}
                      disabled={talepActionLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="talep-complete-btn"
                    >
                      {talepActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Bitir
                    </Button>
                    <Button
                      onClick={() => handleTalepAction('reject')}
                      disabled={talepActionLoading}
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      data-testid="talep-reject-btn"
                    >
                      {talepActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                      Reddet
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedTalep(null); setTalepActionNote(''); }}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onayla ve İşe Çevir Modal */}
      <Dialog open={convertToJobModalOpen} onOpenChange={(open) => {
        setConvertToJobModalOpen(open);
        if (!open) {
          setFormData({ konu: '', aciklama: '', belgeler: [], sube_id: '' });
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <Briefcase className="h-5 w-5" />
              Talebi Onayla ve İşe Çevir
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Kaynak Talep Bilgisi */}
            {selectedTalep && (
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center gap-2 text-sm text-indigo-800">
                  <Inbox className="h-4 w-4" />
                  <span className="font-medium">Kaynak Talep:</span>
                  <span>{selectedTalep.request_number}</span>
                  <span className="text-indigo-600">- {selectedTalep.owner_name}</span>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="convert-konu">İşin Konusu *</Label>
              <Input
                id="convert-konu"
                value={formData.konu}
                onChange={(e) => setFormData(prev => ({ ...prev, konu: e.target.value }))}
                placeholder="İşin konusu"
                data-testid="convert-job-konu"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="convert-aciklama">Açıklama</Label>
              <Textarea
                id="convert-aciklama"
                value={formData.aciklama}
                onChange={(e) => setFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                placeholder="İşin açıklaması"
                rows={3}
                data-testid="convert-job-aciklama"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="convert-sube">Şube</Label>
              <Select
                value={formData.sube_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, sube_id: value }))}
              >
                <SelectTrigger data-testid="convert-job-sube">
                  <SelectValue placeholder="Şube seçin (opsiyonel)" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Seçilmezse kullanıcının şubesi kullanılır</p>
            </div>
            
            {/* Belge Yükleme */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Upload className="h-4 w-4" />
                Belgeler
              </Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    files.forEach(file => {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setFormData(prev => ({
                          ...prev,
                          belgeler: [...prev.belgeler, {
                            name: file.name,
                            type: file.type,
                            size: file.size,
                            data: event.target.result
                          }]
                        }));
                      };
                      reader.readAsDataURL(file);
                    });
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="convert-job-file-upload"
                  data-testid="convert-job-file-input"
                />
                <label htmlFor="convert-job-file-upload" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Belge yüklemek için tıklayın</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Resim dosyaları</p>
                </label>
              </div>
              
              {/* Yüklenen Belgeler Listesi */}
              {formData.belgeler && formData.belgeler.length > 0 && (
                <div className="space-y-1 mt-2">
                  <p className="text-xs text-gray-500 font-medium">Ekli Belgeler ({formData.belgeler.length})</p>
                  {formData.belgeler.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-500" />
                        <span className="truncate max-w-[250px]">{doc.name || doc.filename || `Belge ${idx + 1}`}</span>
                        {doc.size && (
                          <span className="text-xs text-gray-400">({(doc.size / 1024).toFixed(1)} KB)</span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            belgeler: prev.belgeler.filter((_, i) => i !== idx)
                          }));
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setConvertToJobModalOpen(false);
                setFormData({ konu: '', aciklama: '', belgeler: [], sube_id: '' });
              }}
              data-testid="convert-job-cancel"
            >
              İptal
            </Button>
            <Button 
              onClick={handleSubmitConvertToJob}
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="convert-job-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Briefcase className="h-4 w-4 mr-2" />
                  Onayla ve İşe Çevir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yöneticiden İş Talebi Modal */}
      <Dialog open={managerRequestModalOpen} onOpenChange={setManagerRequestModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Send className="h-5 w-5" />
              Yöneticiden İş Talebi
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="mr-konu">İşin Konusu *</Label>
              <Input
                id="mr-konu"
                value={managerRequestFormData.konu}
                onChange={(e) => setManagerRequestFormData(prev => ({ ...prev, konu: e.target.value }))}
                placeholder="Talep edilen işin konusu"
                data-testid="manager-request-konu"
              />
            </div>
            
            {/* Sorular */}
            {managerRequestQuestions.map((question) => (
              <div key={question.id} className="space-y-2">
                <Label htmlFor={`mr-${question.id}`}>
                  {question.label} {question.required && '*'}
                </Label>
                {question.type === 'textarea' ? (
                  <Textarea
                    id={`mr-${question.id}`}
                    value={managerRequestFormData.answers[question.id] || ''}
                    onChange={(e) => setManagerRequestFormData(prev => ({
                      ...prev,
                      answers: { ...prev.answers, [question.id]: e.target.value }
                    }))}
                    placeholder={question.label}
                    rows={3}
                    data-testid={`manager-request-${question.id}`}
                  />
                ) : (
                  <Input
                    id={`mr-${question.id}`}
                    value={managerRequestFormData.answers[question.id] || ''}
                    onChange={(e) => setManagerRequestFormData(prev => ({
                      ...prev,
                      answers: { ...prev.answers, [question.id]: e.target.value }
                    }))}
                    placeholder={question.label}
                    data-testid={`manager-request-${question.id}`}
                  />
                )}
              </div>
            ))}
            
            <div className="space-y-2">
              <Label htmlFor="mr-oncelik">Öncelik</Label>
              <Select 
                value={managerRequestFormData.oncelik} 
                onValueChange={(value) => setManagerRequestFormData(prev => ({ ...prev, oncelik: value }))}
              >
                <SelectTrigger data-testid="manager-request-oncelik">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Düşük</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Yüksek</SelectItem>
                  <SelectItem value="urgent">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Belgeler */}
            <div className="space-y-2">
              <Label>Belgeler</Label>
              {managerRequestFormData.belgeler.length > 0 && (
                <div className="space-y-2 mb-2">
                  {managerRequestFormData.belgeler.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-gray-500" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeManagerRequestFile(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="mr-file-upload"
                  className="hidden"
                  multiple
                  onChange={handleManagerRequestFileUpload}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('mr-file-upload').click()}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Belge Ekle
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManagerRequestModalOpen(false)}>
              İptal
            </Button>
            <Button
              onClick={handleCreateManagerRequest}
              disabled={managerRequestSubmitting || !managerRequestFormData.konu.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="submit-manager-request"
            >
              {managerRequestSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Talep Gönder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yöneticiden İstenen İş Detay Modal */}
      <Dialog open={!!selectedManagerRequest} onOpenChange={() => setSelectedManagerRequest(null)}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Eye className="h-5 w-5" />
              Talep Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedManagerRequest && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Konu</Label>
                  <p className="font-medium">{selectedManagerRequest.konu}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Tarih</Label>
                  <p className="font-medium">
                    {new Date(selectedManagerRequest.created_at).toLocaleDateString('tr-TR')}
                    <span className="text-gray-500 text-sm ml-2">
                      {new Date(selectedManagerRequest.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </p>
                </div>
              </div>
              
              {managerRequestQuestions.map((question) => (
                selectedManagerRequest.answers?.[question.id] && (
                  <div key={question.id}>
                    <Label className="text-gray-500 text-xs">{question.label}</Label>
                    <p className="text-sm mt-1 p-2 bg-gray-50 rounded">{selectedManagerRequest.answers[question.id]}</p>
                  </div>
                )
              ))}
              
              {selectedManagerRequest.belgeler && selectedManagerRequest.belgeler.length > 0 && (
                <div>
                  <Label className="text-gray-500 text-xs">Belgeler ({selectedManagerRequest.belgeler.length})</Label>
                  <div className="space-y-2 mt-2">
                    {selectedManagerRequest.belgeler.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{doc.name}</span>
                        </div>
                        {(doc.url || doc.data) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const fileSrc = doc.url || doc.data;
                              if (fileSrc.startsWith('data:')) {
                                const link = document.createElement('a');
                                link.href = fileSrc;
                                link.download = doc.name;
                                link.click();
                              } else {
                                window.open(fileSrc, '_blank');
                              }
                            }}
                            className="text-blue-600"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedManagerRequest.status_note && (
                <div>
                  <Label className="text-gray-500 text-xs">Durum Notu</Label>
                  <p className="text-sm mt-1 p-2 bg-yellow-50 rounded">{selectedManagerRequest.status_note}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setSelectedManagerRequest(null)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Job Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Yeni İş Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Otomatik Tarih ve Şube Seçimi */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                <div className="flex items-center gap-2 mt-1 p-2 bg-gray-50 rounded-md">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{new Date().toLocaleDateString('tr-TR')}</span>
                  <span className="text-gray-500 text-sm">(Otomatik)</span>
                </div>
              </div>
              <div>
                <Label htmlFor="sube">Şube</Label>
                <Select
                  value={formData.sube_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, sube_id: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="job-sube-select">
                    <SelectValue placeholder={branchesLoading ? "Yükleniyor..." : "Şube seçin"} />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* İşin Konusu */}
            <div>
              <Label htmlFor="konu">İşin Konusu *</Label>
              <Input
                id="konu"
                value={formData.konu}
                onChange={(e) => setFormData(prev => ({ ...prev, konu: e.target.value }))}
                placeholder="İşin konusunu yazın..."
                className="mt-1"
                data-testid="job-konu-input"
              />
            </div>
            
            {/* Açıklama */}
            <div>
              <Label htmlFor="aciklama">Açıklama</Label>
              <Textarea
                id="aciklama"
                value={formData.aciklama}
                onChange={(e) => setFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                placeholder="İş hakkında detaylı açıklama yazın..."
                className="mt-1"
                rows={4}
                data-testid="job-aciklama-input"
              />
            </div>
            
            {/* Belgeler */}
            <div>
              <Label>Belgeler</Label>
              <div className="mt-2 border-2 border-dashed border-gray-200 rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Belge yüklemek için tıklayın</span>
                  <span className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Resim</span>
                </label>
              </div>
              
              {/* Uploaded Files List */}
              {formData.belgeler.length > 0 && (
                <div className="mt-3 space-y-2">
                  {formData.belgeler.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-500" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-gray-400">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="submit-job-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                'Kaydet'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              İş Detayı
              {selectedJob && getStatusBadge(selectedJob.status)}
            </DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-4 py-4">
              
              {/* ORİJİNAL HALİ */}
              <div className="border-2 border-blue-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 px-4 py-2 border-b border-blue-200">
                  <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Orijinal Hali
                  </h3>
                  <p className="text-xs text-blue-600 mt-1">
                    {selectedJob.created_at ? new Date(selectedJob.created_at).toLocaleDateString('tr-TR', { 
                      day: '2-digit', month: '2-digit', year: 'numeric', 
                      hour: '2-digit', minute: '2-digit' 
                    }) : '-'} • {selectedJob.created_by_name || '-'}
                  </p>
                </div>
                <div className="p-4 bg-white space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">Şube</span>
                      <p className="font-medium text-sm">{selectedJob.sube_adi || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">İşin Konusu</span>
                      <p className="font-medium text-sm">{selectedJob.original_konu || selectedJob.konu || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Açıklama</span>
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{selectedJob.original_aciklama || selectedJob.aciklama || 'Açıklama yok'}</p>
                  </div>
                  {/* Orijinal Belgeler - belgeler, original_belgeler, veya source_request'ten */}
                  {(() => {
                    // Tüm belgeleri topla
                    const allBelgeler = [
                      ...(selectedJob.original_belgeler || []),
                      ...(selectedJob.belgeler || []),
                      ...(selectedJob.source_request?.attachments || []),
                      ...(selectedJob.source_request?.documents || [])
                    ];
                    
                    // Sadece corrections yoksa veya orijinal belgeler gösterilecekse
                    const belgelerToShow = selectedJob.original_belgeler?.length > 0 
                      ? selectedJob.original_belgeler 
                      : ((!selectedJob.corrections || selectedJob.corrections.length === 0) ? allBelgeler : []);
                    
                    if (belgelerToShow.length === 0) return null;
                    
                    return (
                      <div>
                        <span className="text-xs text-gray-500">Belgeler ({belgelerToShow.length})</span>
                        <div className="mt-2 space-y-2">
                          {belgelerToShow.map((belge, idx) => {
                            const fileSrc = belge.url || belge.data;
                            const fileName = belge.name || belge.filename || 'Belge';
                            const isImage = belge.type?.startsWith('image/') || fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                            const isPdf = belge.type === 'application/pdf' || fileName?.match(/\.pdf$/i);
                            
                            return (
                              <div key={idx} className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <File className="h-4 w-4 text-blue-500" />
                                  <span className="text-sm max-w-[200px] truncate">{fileName}</span>
                                  {belge.size && (
                                    <span className="text-xs text-gray-400">({(belge.size / 1024).toFixed(1)} KB)</span>
                                  )}
                                </div>
                                {fileSrc && (
                                  <div className="flex gap-1">
                                    {/* Önizle butonu */}
                                    {isImage && (
                                      <Button variant="ghost" size="sm" className="h-7 px-2 text-blue-600" onClick={() => {
                                        const modal = document.createElement('div');
                                        modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:9999;cursor:pointer';
                                        modal.onclick = () => modal.remove();
                                        const img = document.createElement('img');
                                        img.src = fileSrc;
                                        img.style.cssText = 'max-width:90%;max-height:90%;object-fit:contain;border-radius:8px';
                                        modal.appendChild(img);
                                        document.body.appendChild(modal);
                                      }}>
                                        <Eye className="h-3 w-3 mr-1" />
                                        Önizle
                                      </Button>
                                    )}
                                    {/* PDF için görüntüle */}
                                    {isPdf && (
                                      <Button variant="ghost" size="sm" className="h-7 px-2 text-blue-600" onClick={() => {
                                        window.open(fileSrc, '_blank');
                                      }}>
                                        <Eye className="h-3 w-3 mr-1" />
                                        Görüntüle
                                      </Button>
                                    )}
                                    {/* İndir butonu */}
                                    <Button variant="ghost" size="sm" className="h-7 px-2 text-green-600" onClick={() => {
                                      if (fileSrc.startsWith('data:')) {
                                        try {
                                          const byteString = atob(fileSrc.split(',')[1]);
                                          const mimeType = fileSrc.match(/data:([^;]+);/)?.[1] || 'application/octet-stream';
                                          const ab = new ArrayBuffer(byteString.length);
                                          const ia = new Uint8Array(ab);
                                          for (let i = 0; i < byteString.length; i++) {
                                            ia[i] = byteString.charCodeAt(i);
                                          }
                                          const blob = new Blob([ab], { type: mimeType });
                                          const url = URL.createObjectURL(blob);
                                          const link = document.createElement('a');
                                          link.href = url;
                                          link.download = fileName;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                          URL.revokeObjectURL(url);
                                        } catch (e) {
                                          console.error('Download error:', e);
                                          window.open(fileSrc, '_blank');
                                        }
                                      } else {
                                        const link = document.createElement('a');
                                        link.href = fileSrc;
                                        link.download = fileName;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }
                                    }}>
                                      <Download className="h-3 w-3 mr-1" />
                                      İndir
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* ALINAN HİZMET TİPİ İŞLER İÇİN DETAYLAR */}
              {selectedJob.type === 'alinan_hizmet' && selectedJob.source_service && (
                <div className="border-2 border-emerald-200 rounded-lg overflow-hidden">
                  <div className="bg-emerald-50 px-4 py-2 border-b border-emerald-200">
                    <h3 className="font-semibold text-emerald-800 flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Alınan Hizmet Detayları
                    </h3>
                  </div>
                  <div className="p-4 bg-white space-y-4">
                    {/* Hizmet Bilgileri */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-500">Hizmet No</Label>
                        <p className="font-medium text-blue-600">{selectedJob.source_service.seri_no || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Hizmet Adı</Label>
                        <p className="font-medium">{selectedJob.source_service.service_name}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Cari Hesap</Label>
                        <p className="font-medium">{selectedJob.source_service.cari_hesap_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Şube</Label>
                        <p className="font-medium">{selectedJob.source_service.branch_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Toplam Tutar</Label>
                        <p className="font-medium text-emerald-600">
                          {selectedJob.source_service.total_amount?.toLocaleString('tr-TR')} {selectedJob.source_service.currency || 'TL'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Yetkili Bilgileri */}
                    {(selectedJob.source_service.yetkili_adi || selectedJob.source_service.yetkili_telefon) && (
                      <div className="border-t pt-3">
                        <Label className="text-gray-500 text-xs">Yetkili Bilgileri</Label>
                        <div className="flex gap-4 mt-1 text-sm">
                          {selectedJob.source_service.yetkili_adi && (
                            <span><User className="h-3 w-3 inline mr-1" />{selectedJob.source_service.yetkili_adi}</span>
                          )}
                          {selectedJob.source_service.yetkili_telefon && (
                            <span className="text-gray-600">{selectedJob.source_service.yetkili_telefon}</span>
                          )}
                          {selectedJob.source_service.yetkili_email && (
                            <span className="text-gray-600">{selectedJob.source_service.yetkili_email}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Açıklama */}
                    {selectedJob.source_service.description && (
                      <div className="border-t pt-3">
                        <Label className="text-gray-500 text-xs">Hizmet Açıklaması</Label>
                        <p className="mt-1 text-sm bg-gray-50 p-2 rounded">{selectedJob.source_service.description}</p>
                      </div>
                    )}

                    {/* Hakediş Bilgileri */}
                    {selectedJob.source_service.hakedis && (
                      <div className="border-t pt-3">
                        <Label className="text-gray-500 text-xs">Hakediş Detayı</Label>
                        <div className="mt-2 bg-blue-50 p-3 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{selectedJob.source_service.hakedis.hakedis_no}. Hakediş</span>
                            <Badge className="bg-emerald-100 text-emerald-700">
                              {selectedJob.source_service.hakedis.tutar?.toLocaleString('tr-TR')} {selectedJob.source_service.currency || 'TL'}
                            </Badge>
                          </div>
                          {selectedJob.source_service.hakedis.aciklama && (
                            <p className="text-sm text-gray-600 mt-1">{selectedJob.source_service.hakedis.aciklama}</p>
                          )}
                          <div className="flex gap-4 mt-2 text-xs text-gray-500">
                            {selectedJob.source_service.hakedis.muhasebe_onay_yapan && (
                              <span>Gönderen: {selectedJob.source_service.hakedis.muhasebe_onay_yapan}</span>
                            )}
                            {selectedJob.source_service.hakedis.muhasebe_onay_tarihi && (
                              <span>Tarih: {new Date(selectedJob.source_service.hakedis.muhasebe_onay_tarihi).toLocaleString('tr-TR')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sözleşme Belgeleri */}
                    {selectedJob.source_service.contract_documents?.length > 0 && (
                      <div className="border-t pt-3">
                        <Label className="text-gray-500 text-xs">Sözleşme Belgeleri ({selectedJob.source_service.contract_documents.length})</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedJob.source_service.contract_documents.map((doc, idx) => {
                            const fileName = doc.original_name || doc.name || `Belge ${idx + 1}`;
                            const fileUrl = doc.url?.startsWith('http') ? doc.url : `${process.env.REACT_APP_BACKEND_URL}${doc.url}`;
                            return (
                              <div key={idx} className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                                <File className="h-4 w-4 text-blue-500" />
                                <span className="text-sm">{fileName}</span>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 px-2 text-blue-600" 
                                  onClick={() => window.open(fileUrl, '_blank')}
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* SATIN ALMADAN GELEN İŞLER İÇİN DETAYLAR */}
              {selectedJob.source_type?.includes('purchasing') && selectedJob.purchasing_job_details && (
                <>
                  {/* İletme Bilgisi */}
                  {selectedJob.purchasing_job_details.forwarded_from && (
                    <div className="border-2 border-indigo-200 rounded-lg overflow-hidden">
                      <div className="bg-indigo-50 px-4 py-2 border-b border-indigo-200">
                        <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
                          <Send className="h-4 w-4" />
                          İletme Bilgisi
                        </h3>
                      </div>
                      <div className="p-4 bg-white space-y-2 text-sm">
                        <p><span className="text-gray-500">Kaynak:</span> <Badge className="bg-indigo-100 text-indigo-700 ml-1">{selectedJob.purchasing_job_details.forwarded_from === 'branch' ? 'Şube Yönetimi' : selectedJob.purchasing_job_details.forwarded_from}</Badge></p>
                        {selectedJob.purchasing_job_details.forwarded_by_name && (
                          <p><span className="text-gray-500">İleten:</span> <span className="font-medium ml-1">{selectedJob.purchasing_job_details.forwarded_by_name}</span></p>
                        )}
                        {selectedJob.purchasing_job_details.forwarded_at && (
                          <p><span className="text-gray-500">İletme Tarihi:</span> <span className="ml-1">{new Date(selectedJob.purchasing_job_details.forwarded_at).toLocaleString('tr-TR')}</span></p>
                        )}
                        {selectedJob.purchasing_job_details.forwarded_note && (
                          <div>
                            <span className="text-gray-500">İletme Notu:</span>
                            <p className="mt-1 bg-indigo-50 p-2 rounded border border-indigo-200 text-indigo-700">{selectedJob.purchasing_job_details.forwarded_note}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Ekli Belgeler */}
                  {selectedJob.purchasing_job_details.belgeler?.length > 0 && (
                    <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                          <File className="h-4 w-4" />
                          Ekli Belgeler ({selectedJob.purchasing_job_details.belgeler.length})
                        </h3>
                      </div>
                      <div className="p-4 bg-white">
                        <div className="flex flex-wrap gap-2">
                          {selectedJob.purchasing_job_details.belgeler.map((belge, idx) => {
                            const fileName = belge.name || belge.filename || `Belge ${idx + 1}`;
                            const fileSrc = belge.data || belge.url || belge.src;
                            return (
                              <div key={idx} className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                                <File className="h-4 w-4 text-blue-500" />
                                <span className="text-sm">{fileName}</span>
                                {fileSrc && (
                                  <Button variant="ghost" size="sm" className="h-6 px-2 text-blue-600" onClick={() => window.open(fileSrc, '_blank')}>
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SEÇİLEN TEKLİF */}
                  {selectedJob.purchasing_job_details.selected_teklif && (
                    <div className="border-2 border-green-300 rounded-lg overflow-hidden">
                      <div className="bg-green-100 px-4 py-2 border-b border-green-300">
                        <h3 className="font-bold text-green-800 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          SEÇİLEN TEKLİF
                        </h3>
                      </div>
                      <div className="p-4 bg-green-50 space-y-3">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Firma Adı:</span>
                            <p className="font-semibold text-gray-800">{selectedJob.purchasing_job_details.selected_teklif.firma_adi || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Toplam Fiyat:</span>
                            <p className="font-semibold text-green-700">{selectedJob.purchasing_job_details.selected_teklif.toplam_fiyat || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Ödeme Planı:</span>
                            <p className="font-semibold text-gray-800">{selectedJob.purchasing_job_details.selected_teklif.odeme_plani || '-'}</p>
                          </div>
                        </div>
                        {selectedJob.purchasing_job_details.selected_teklif.aciklama && (
                          <div>
                            <span className="text-gray-600 text-sm">Teklif Açıklaması:</span>
                            <p className="mt-1 text-gray-700 bg-white p-2 rounded border">{selectedJob.purchasing_job_details.selected_teklif.aciklama}</p>
                          </div>
                        )}
                        {selectedJob.purchasing_job_details.selected_teklif.belgeler?.length > 0 && (
                          <div>
                            <span className="text-gray-600 text-sm">Belgeler:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {selectedJob.purchasing_job_details.selected_teklif.belgeler.map((belge, idx) => (
                                <div key={idx} className="flex items-center gap-1 bg-white px-2 py-1 rounded text-sm border">
                                  <File className="h-3 w-3 text-blue-500" />
                                  <span>{belge.name || `Belge ${idx + 1}`}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Yönetim Notu */}
                        {selectedJob.purchasing_job_details.management_note && (
                          <div className="mt-2 pt-2 border-t border-green-200">
                            <span className="text-gray-600 text-sm">Yönetim Notu:</span>
                            <p className="mt-1 font-medium text-green-800 bg-white p-2 rounded border border-green-200">{selectedJob.purchasing_job_details.management_note}</p>
                          </div>
                        )}
                        {selectedJob.purchasing_job_details.management_approved_by_name && (
                          <p className="text-xs text-green-700">
                            Onaylayan: {selectedJob.purchasing_job_details.management_approved_by_name}
                            {selectedJob.purchasing_job_details.management_approved_at && ` (${new Date(selectedJob.purchasing_job_details.management_approved_at).toLocaleString('tr-TR')})`}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* HAZIRLANAN TALEP */}
                  {selectedJob.purchasing_job_details.prepared_request && (
                    <div className="border-2 border-purple-200 rounded-lg overflow-hidden">
                      <div className="bg-purple-50 px-4 py-2 border-b border-purple-200">
                        <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                          <ClipboardList className="h-4 w-4" />
                          Hazırlanan Talep
                        </h3>
                      </div>
                      <div className="p-4 bg-white space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Tarih</span>
                            <p className="font-medium">{selectedJob.purchasing_job_details.prepared_request.tarih || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Saat</span>
                            <p className="font-medium">{selectedJob.purchasing_job_details.prepared_request.saat || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">İşin Adı</span>
                            <p className="font-medium">{selectedJob.purchasing_job_details.prepared_request.is_adi || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Hazırlayan</span>
                            <p className="font-medium">{selectedJob.purchasing_job_details.prepared_request.hazirlayan || '-'}</p>
                          </div>
                        </div>
                        {selectedJob.purchasing_job_details.prepared_request.aciklama && (
                          <div>
                            <span className="text-gray-500 text-sm">Açıklama</span>
                            <p className="text-gray-700 mt-1">{selectedJob.purchasing_job_details.prepared_request.aciklama}</p>
                          </div>
                        )}
                        {selectedJob.purchasing_job_details.prepared_request.created_at && (
                          <p className="text-xs text-gray-500">{new Date(selectedJob.purchasing_job_details.prepared_request.created_at).toLocaleString('tr-TR')}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ONAYLANAN SİPARİŞ */}
                  {selectedJob.order_details && (
                    <div className="border-2 border-emerald-300 rounded-lg overflow-hidden">
                      <div className="bg-emerald-100 px-4 py-2 border-b border-emerald-300">
                        <h3 className="font-bold text-emerald-800 flex items-center gap-2">
                          <ShoppingCart className="h-4 w-4" />
                          ONAYLANAN SİPARİŞ
                          <Badge className="bg-emerald-600 text-white">Onaylandı</Badge>
                        </h3>
                      </div>
                      <div className="p-4 bg-emerald-50 space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">Hedef Şube:</span>
                            <p className="font-semibold">{selectedJob.order_details.branch_name || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Tedarikçi (Cari Hesap):</span>
                            <p className="font-semibold">{selectedJob.order_details.cari_hesap_name || selectedJob.cari_hesap_name || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Sipariş Tarihi:</span>
                            <p className="font-semibold">{selectedJob.order_details.entry_datetime ? new Date(selectedJob.order_details.entry_datetime).toLocaleString('tr-TR') : '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Tahmini Geliş:</span>
                            <p className="font-semibold">{selectedJob.order_details.estimated_arrival_datetime ? new Date(selectedJob.order_details.estimated_arrival_datetime).toLocaleString('tr-TR') : '-'}</p>
                          </div>
                        </div>
                        
                        {/* Sipariş Kalemleri */}
                        {selectedJob.order_details.items?.length > 0 && (
                          <div>
                            <span className="text-gray-600 text-sm font-medium">Sipariş Kalemleri:</span>
                            <table className="w-full mt-2 text-sm">
                              <thead className="bg-emerald-200">
                                <tr>
                                  <th className="text-left px-3 py-2 font-medium">Ürün</th>
                                  <th className="text-center px-3 py-2 font-medium">Miktar</th>
                                  <th className="text-right px-3 py-2 font-medium">Fiyat</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white">
                                {selectedJob.order_details.items.map((item, idx) => (
                                  <tr key={idx} className="border-b">
                                    <td className="px-3 py-2">{item.product_name || '-'}</td>
                                    <td className="px-3 py-2 text-center">{item.quantity} {item.unit || 'Adet'}</td>
                                    <td className="px-3 py-2 text-right font-medium">{item.purchase_price ? `${parseFloat(item.purchase_price).toLocaleString('tr-TR')} ₺` : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        
                        {selectedJob.order_details.description && (
                          <div>
                            <span className="text-gray-600 text-sm">Açıklama:</span>
                            <p className="mt-1 text-gray-700">{selectedJob.order_details.description}</p>
                          </div>
                        )}
                        
                        {selectedJob.order_details.approved_by_name && (
                          <p className="text-xs text-emerald-700">
                            Onaylayan: {selectedJob.order_details.approved_by_name}
                            {selectedJob.order_details.approved_at && ` (${new Date(selectedJob.order_details.approved_at).toLocaleString('tr-TR')})`}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* İŞLEMLER VE KAYITLAR - Yapılacak işler için detaylı gösterim */}
              {(selectedJob.transactions?.length > 0 || selectedJob.created_records?.length > 0) && (
                <div className="border-2 border-indigo-200 rounded-lg overflow-hidden">
                  <div className="bg-indigo-50 px-4 py-2 border-b border-indigo-200">
                    <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
                      <ListChecks className="h-4 w-4" />
                      İşlemler ve Kayıtlar
                    </h3>
                  </div>
                  <div className="p-4 bg-white space-y-4">
                    {/* Yapılan İşlemler */}
                    {selectedJob.transactions?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <Receipt className="h-4 w-4 text-indigo-500" />
                          Yapılan İşlemler ({selectedJob.transactions.length})
                        </h4>
                        <div className="space-y-2">
                          {selectedJob.transactions.map((tx, idx) => (
                            <div key={idx} className="bg-gray-50 border rounded-lg p-3">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  <Badge className={
                                    tx.type === 'giris' ? 'bg-green-100 text-green-700 border-green-300' : 
                                    tx.type === 'cikis' ? 'bg-red-100 text-red-700 border-red-300' : 
                                    tx.type === 'cek' ? 'bg-purple-100 text-purple-700 border-purple-300' :
                                    tx.type === 'alacak' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                                    tx.type === 'borc' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                                    'bg-gray-100 text-gray-700 border-gray-300'
                                  }>
                                    {tx.type === 'giris' ? 'Giriş' : 
                                     tx.type === 'cikis' ? 'Çıkış' : 
                                     tx.type === 'cek' ? 'Çek' : 
                                     tx.type === 'alacak' ? 'Alacak' : 
                                     tx.type === 'borc' ? 'Borç' : tx.type}
                                  </Badge>
                                  <span className="font-bold text-lg text-gray-800">
                                    {(parseFloat(tx.tutar) || parseFloat(tx.form_data?.miktar) || parseFloat(tx.form_data?.tutar) || parseFloat(tx.form_data?.toplam_tutar) || 0).toLocaleString('tr-TR')} ₺
                                  </span>
                                </div>
                                <span className="text-sm text-gray-500">
                                  {tx.tarih ? new Date(tx.tarih).toLocaleDateString('tr-TR') : '-'}
                                </span>
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                                {tx.cari_hesap_adi && (
                                  <div>
                                    <span className="text-gray-500">Cari Hesap:</span>
                                    <span className="ml-1 font-medium">{tx.cari_hesap_adi}</span>
                                  </div>
                                )}
                                {tx.hesap_adi && (
                                  <div>
                                    <span className="text-gray-500">Hesap:</span>
                                    <span className="ml-1 font-medium">{tx.hesap_adi}</span>
                                  </div>
                                )}
                                {tx.vade_tarihi && (
                                  <div>
                                    <span className="text-gray-500">Vade:</span>
                                    <span className="ml-1 font-medium">{new Date(tx.vade_tarihi).toLocaleDateString('tr-TR')}</span>
                                  </div>
                                )}
                                {tx.created_by_name && (
                                  <div>
                                    <span className="text-gray-500">Ekleyen:</span>
                                    <span className="ml-1 font-medium">{tx.created_by_name}</span>
                                  </div>
                                )}
                              </div>
                              {tx.aciklama && (
                                <div className="mt-2 text-sm text-gray-600 bg-white p-2 rounded border">
                                  <span className="text-gray-500">Açıklama:</span> {tx.aciklama}
                                </div>
                              )}
                              {/* İşleme ait belgeler */}
                              {tx.belgeler && tx.belgeler.length > 0 && (
                                <div className="mt-2">
                                  <span className="text-xs text-gray-500 font-medium">Belgeler ({tx.belgeler.length})</span>
                                  <div className="mt-1 flex flex-wrap gap-2">
                                    {tx.belgeler.map((belge, belgeIdx) => {
                                      const fileSrc = belge.url || belge.data;
                                      const fileName = belge.name || belge.filename || `Belge ${belgeIdx + 1}`;
                                      const isImage = belge.type?.startsWith('image/') || fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                      const isPdf = belge.type === 'application/pdf' || fileName?.match(/\.pdf$/i);
                                      
                                      return (
                                        <div key={belgeIdx} className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-2 py-1.5 rounded-lg text-xs">
                                          <File className="h-3.5 w-3.5 text-blue-500" />
                                          <span className="max-w-[100px] truncate font-medium" title={fileName}>{fileName}</span>
                                          {fileSrc && (
                                            <div className="flex gap-1">
                                              {/* Önizle butonu (resimler için) */}
                                              {isImage && (
                                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-blue-600" title="Önizle" onClick={() => {
                                                  const modal = document.createElement('div');
                                                  modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:9999;cursor:pointer';
                                                  modal.onclick = () => modal.remove();
                                                  const img = document.createElement('img');
                                                  img.src = fileSrc;
                                                  img.style.cssText = 'max-width:90%;max-height:90%;object-fit:contain;border-radius:8px';
                                                  modal.appendChild(img);
                                                  document.body.appendChild(modal);
                                                }}>
                                                  <Eye className="h-3 w-3" />
                                                </Button>
                                              )}
                                              {/* PDF için görüntüle */}
                                              {isPdf && (
                                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-blue-600" title="Görüntüle" onClick={() => {
                                                  window.open(fileSrc, '_blank');
                                                }}>
                                                  <Eye className="h-3 w-3" />
                                                </Button>
                                              )}
                                              {/* İndir butonu */}
                                              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-green-600" title="İndir" onClick={() => {
                                                if (fileSrc.startsWith('data:')) {
                                                  try {
                                                    const byteString = atob(fileSrc.split(',')[1]);
                                                    const mimeType = fileSrc.match(/data:([^;]+);/)?.[1] || 'application/octet-stream';
                                                    const ab = new ArrayBuffer(byteString.length);
                                                    const ia = new Uint8Array(ab);
                                                    for (let i = 0; i < byteString.length; i++) {
                                                      ia[i] = byteString.charCodeAt(i);
                                                    }
                                                    const blob = new Blob([ab], { type: mimeType });
                                                    const url = URL.createObjectURL(blob);
                                                    const link = document.createElement('a');
                                                    link.href = url;
                                                    link.download = fileName;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                    URL.revokeObjectURL(url);
                                                  } catch (e) {
                                                    console.error('Download error:', e);
                                                    window.open(fileSrc, '_blank');
                                                  }
                                                } else {
                                                  const link = document.createElement('a');
                                                  link.href = fileSrc;
                                                  link.download = fileName;
                                                  document.body.appendChild(link);
                                                  link.click();
                                                  document.body.removeChild(link);
                                                }
                                              }}>
                                                <Download className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Toplam Özet */}
                        <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 text-sm">
                          {selectedJob.transactions.filter(t => t.type === 'giris').length > 0 && (
                            <div className="flex items-center gap-1 text-green-700 bg-green-50 px-3 py-1 rounded-full">
                              <ArrowDownCircle className="h-4 w-4" />
                              <span className="font-semibold">Giriş Toplamı:</span>
                              <span>{selectedJob.transactions.filter(t => t.type === 'giris').reduce((a, b) => a + (parseFloat(b.tutar) || parseFloat(b.form_data?.miktar) || 0), 0).toLocaleString('tr-TR')} ₺</span>
                            </div>
                          )}
                          {selectedJob.transactions.filter(t => t.type === 'cikis').length > 0 && (
                            <div className="flex items-center gap-1 text-red-700 bg-red-50 px-3 py-1 rounded-full">
                              <ArrowUpCircle className="h-4 w-4" />
                              <span className="font-semibold">Çıkış Toplamı:</span>
                              <span>{selectedJob.transactions.filter(t => t.type === 'cikis').reduce((a, b) => a + (parseFloat(b.tutar) || parseFloat(b.form_data?.miktar) || 0), 0).toLocaleString('tr-TR')} ₺</span>
                            </div>
                          )}
                          {selectedJob.transactions.filter(t => t.type === 'cek').length > 0 && (
                            <div className="flex items-center gap-1 text-purple-700 bg-purple-50 px-3 py-1 rounded-full">
                              <Receipt className="h-4 w-4" />
                              <span className="font-semibold">Çek Toplamı:</span>
                              <span>{selectedJob.transactions.filter(t => t.type === 'cek').reduce((a, b) => a + (parseFloat(b.tutar) || parseFloat(b.form_data?.miktar) || 0), 0).toLocaleString('tr-TR')} ₺</span>
                            </div>
                          )}
                          {selectedJob.transactions.filter(t => t.type === 'alacak').length > 0 && (
                            <div className="flex items-center gap-1 text-blue-700 bg-blue-50 px-3 py-1 rounded-full">
                              <ArrowUp className="h-4 w-4" />
                              <span className="font-semibold">Alacak Toplamı:</span>
                              <span>{selectedJob.transactions.filter(t => t.type === 'alacak').reduce((a, b) => a + (parseFloat(b.tutar) || parseFloat(b.form_data?.toplam_tutar) || 0), 0).toLocaleString('tr-TR')} ₺</span>
                            </div>
                          )}
                          {selectedJob.transactions.filter(t => t.type === 'borc').length > 0 && (
                            <div className="flex items-center gap-1 text-orange-700 bg-orange-50 px-3 py-1 rounded-full">
                              <ArrowDown className="h-4 w-4" />
                              <span className="font-semibold">Borç Toplamı:</span>
                              <span>{selectedJob.transactions.filter(t => t.type === 'borc').reduce((a, b) => a + (parseFloat(b.tutar) || parseFloat(b.form_data?.toplam_tutar) || 0), 0).toLocaleString('tr-TR')} ₺</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Oluşturulan Kayıtlar */}
                    {selectedJob.created_records?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Oluşturulan Kayıtlar ({selectedJob.created_records.length})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedJob.created_records.map((record, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                              <Badge className={
                                record.type === 'giris' ? 'bg-green-100 text-green-700' : 
                                record.type === 'cikis' ? 'bg-red-100 text-red-700' : 
                                record.type === 'cek' ? 'bg-purple-100 text-purple-700' :
                                record.type === 'alacak' ? 'bg-blue-100 text-blue-700' :
                                'bg-orange-100 text-orange-700'
                              }>
                                {record.type === 'giris' ? 'Giriş' : 
                                 record.type === 'cikis' ? 'Çıkış' : 
                                 record.type === 'cek' ? 'Çek' : 
                                 record.type === 'alacak' ? 'Alacak' : 
                                 record.type === 'borc' ? 'Borç' : record.type}
                              </Badge>
                              <span className="font-mono font-semibold text-green-700">{record.seri_no || record.id}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Onay Geçmişi */}
                    <div className="pt-3 border-t">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <ClipboardCheck className="h-4 w-4 text-indigo-500" />
                        Onay Geçmişi
                      </h4>
                      <div className="space-y-2 text-sm">
                        {selectedJob.approved_at && (
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="h-4 w-4" />
                            <span><strong>Onaylayan:</strong> {selectedJob.approved_by_name || '-'}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500">{new Date(selectedJob.approved_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        )}
                        {selectedJob.prepared_at && (
                          <div className="flex items-center gap-2 text-cyan-700">
                            <ClipboardCheck className="h-4 w-4" />
                            <span><strong>Hazırlayan:</strong> {selectedJob.prepared_by_name || '-'}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500">{new Date(selectedJob.prepared_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        )}
                        {selectedJob.board_approved_at && (
                          <div className="flex items-center gap-2 text-indigo-700">
                            <CheckCircle className="h-4 w-4" />
                            <span><strong>YK Onayı:</strong> {selectedJob.board_approved_by_name || '-'}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500">{new Date(selectedJob.board_approved_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        )}
                        {selectedJob.board_approval_note && (
                          <div className="mt-2 bg-indigo-50 p-2 rounded text-indigo-700 text-sm">
                            <strong>YK Notu:</strong> {selectedJob.board_approval_note}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* DEĞİŞİKLİKLER */}
              {selectedJob.corrections && selectedJob.corrections.length > 0 && (
                <div className="space-y-3">
                  {selectedJob.corrections.map((correction, idx) => {
                    const isDuzeltmeGonderildi = correction.tip === 'duzeltme_gonderildi';
                    const borderColor = isDuzeltmeGonderildi ? 'border-green-200' : 'border-amber-200';
                    const bgColor = isDuzeltmeGonderildi ? 'bg-green-50' : 'bg-amber-50';
                    const textColor = isDuzeltmeGonderildi ? 'text-green-800' : 'text-amber-800';
                    const subTextColor = isDuzeltmeGonderildi ? 'text-green-600' : 'text-amber-600';
                    const icon = isDuzeltmeGonderildi ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />;
                    const title = isDuzeltmeGonderildi 
                      ? `${idx + 1}. Düzeltme Gönderildi` 
                      : `${idx + 1}. Düzeltme Talebi`;
                    const author = isDuzeltmeGonderildi 
                      ? (correction.submitted_by_name || 'Muhasebe')
                      : (correction.requested_by_name || 'Yönetim Kurulu');
                    
                    return (
                      <div key={idx} className={`border-2 ${borderColor} rounded-lg overflow-hidden`}>
                        <div className={`${bgColor} px-4 py-2 border-b ${borderColor}`}>
                          <h3 className={`font-semibold ${textColor} flex items-center gap-2`}>
                            {icon}
                            {title}
                          </h3>
                          <p className={`text-xs ${subTextColor} mt-1`}>
                            {correction.tarih ? new Date(correction.tarih).toLocaleDateString('tr-TR', { 
                              day: '2-digit', month: '2-digit', year: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            }) : '-'} • {author}
                          </p>
                        </div>
                        <div className="p-4 bg-white space-y-3">
                          {correction.konu && (
                            <div>
                              <span className="text-xs text-gray-500">Yeni Konu</span>
                              <p className="font-medium text-sm">{correction.konu}</p>
                            </div>
                          )}
                          <div>
                            <span className="text-xs text-gray-500">{isDuzeltmeGonderildi ? 'Düzeltme Notu' : 'Talep Açıklaması'}</span>
                            <p className="text-sm text-gray-700 mt-1">{correction.aciklama || '-'}</p>
                          </div>
                          {correction.belgeler && correction.belgeler.length > 0 && (
                            <div>
                              <span className="text-xs text-gray-500">Belgeler ({correction.belgeler.length})</span>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {correction.belgeler.map((belge, belgeIdx) => (
                                  <div key={belgeIdx} className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg text-sm">
                                    <File className="h-4 w-4 text-blue-500" />
                                    <span className="max-w-[150px] truncate">{belge.name}</span>
                                    {belge.url && (
                                      <div className="flex gap-1">
                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                                          if (belge.type?.startsWith('image/') || belge.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
                                            const modal = document.createElement('div');
                                            modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:9999;cursor:pointer';
                                            modal.onclick = () => modal.remove();
                                            const img = document.createElement('img');
                                            img.src = belge.url;
                                            img.style.cssText = 'max-width:90%;max-height:90%;object-fit:contain;border-radius:8px';
                                            modal.appendChild(img);
                                            document.body.appendChild(modal);
                                          } else {
                                            window.open(belge.url, '_blank');
                                          }
                                        }}>
                                          <Eye className="h-3 w-3" />
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                                          const link = document.createElement('a');
                                          link.href = belge.url;
                                          link.download = belge.name || 'belge';
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}>
                                          <Download className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* RED AÇIKLAMASI */}
              {selectedJob.rejection_reason && (
                <div className="border-2 border-red-200 rounded-lg overflow-hidden">
                  <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                    <h3 className="font-semibold text-red-800 flex items-center gap-2">
                      <Ban className="h-4 w-4" />
                      Reddedildi
                    </h3>
                    <p className="text-xs text-red-600 mt-1">
                      {selectedJob.rejected_at ? new Date(selectedJob.rejected_at).toLocaleDateString('tr-TR', { 
                        day: '2-digit', month: '2-digit', year: 'numeric', 
                        hour: '2-digit', minute: '2-digit' 
                      }) : '-'} • {selectedJob.rejected_by_name || 'Yönetim Kurulu'}
                    </p>
                  </div>
                  <div className="p-4 bg-white">
                    <span className="text-xs text-gray-500">Red Sebebi</span>
                    <p className="text-sm text-gray-700 mt-1">{selectedJob.rejection_reason}</p>
                  </div>
                </div>
              )}

              {/* GÜNCEL DURUM */}
              {selectedJob.corrections && selectedJob.corrections.length > 0 && (
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
                    <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                      <ClipboardCheck className="h-4 w-4" />
                      Güncel Durum
                    </h3>
                  </div>
                  <div className="p-4 bg-white space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-500">Güncel Konu</span>
                        <p className="font-medium text-sm">{selectedJob.konu || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Durum</span>
                        <div className="mt-1">{getStatusBadge(selectedJob.status)}</div>
                      </div>
                    </div>
                    {selectedJob.belgeler && selectedJob.belgeler.length > 0 && (
                      <div>
                        <span className="text-xs text-gray-500">Tüm Belgeler ({selectedJob.belgeler.length})</span>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedJob.belgeler.map((belge, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg text-sm">
                              <File className="h-4 w-4 text-blue-500" />
                              <span className="max-w-[150px] truncate">{belge.name}</span>
                              {belge.url && (
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                                    if (belge.type?.startsWith('image/') || belge.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
                                      const modal = document.createElement('div');
                                      modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:9999;cursor:pointer';
                                      modal.onclick = () => modal.remove();
                                      const img = document.createElement('img');
                                      img.src = belge.url;
                                      img.style.cssText = 'max-width:90%;max-height:90%;object-fit:contain;border-radius:8px';
                                      modal.appendChild(img);
                                      document.body.appendChild(modal);
                                    } else {
                                      window.open(belge.url, '_blank');
                                    }
                                  }}>
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = belge.url;
                                    link.download = belge.name || 'belge';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}>
                                    <Download className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            {/* Onaylanan işler için işlem butonları */}
            {selectedJob?.status === 'approved' && (
              <div className="w-full space-y-4 mb-3 pb-3 border-b">
                {/* İş Bilgileri Düzenleme Alanı */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <Edit className="h-4 w-4" />
                    İş Bilgilerini Düzenle
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm">İş Başlığı</Label>
                      <Input
                        value={selectedJob.work_title || selectedJob.konu || ''}
                        onChange={(e) => setSelectedJob(prev => ({ ...prev, work_title: e.target.value }))}
                        placeholder="İş başlığı girin..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Açıklama / Notlar</Label>
                      <Textarea
                        value={selectedJob.work_notes || ''}
                        onChange={(e) => setSelectedJob(prev => ({ ...prev, work_notes: e.target.value }))}
                        placeholder="İş hakkında notlarınızı yazın..."
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Belge Ekle</Label>
                      <div className="mt-1">
                        <Input
                          type="file"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            files.forEach(file => {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setSelectedJob(prev => ({
                                  ...prev,
                                  work_documents: [
                                    ...(prev.work_documents || []),
                                    {
                                      name: file.name,
                                      type: file.type,
                                      size: file.size,
                                      data: event.target.result
                                    }
                                  ]
                                }));
                              };
                              reader.readAsDataURL(file);
                            });
                            e.target.value = '';
                          }}
                          className="text-sm"
                        />
                      </div>
                      {selectedJob.work_documents && selectedJob.work_documents.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedJob.work_documents.map((doc, idx) => (
                            <div key={idx} className="flex items-center gap-1 bg-white px-2 py-1 rounded text-xs border">
                              <File className="h-3 w-3 text-blue-500" />
                              <span className="max-w-[100px] truncate">{doc.name}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 text-red-500"
                                onClick={() => {
                                  setSelectedJob(prev => ({
                                    ...prev,
                                    work_documents: prev.work_documents.filter((_, i) => i !== idx)
                                  }));
                                }}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/update-work-info`, {
                            method: 'PUT',
                            headers: getAuthHeaders(),
                            body: JSON.stringify({
                              work_title: selectedJob.work_title,
                              work_notes: selectedJob.work_notes,
                              work_documents: selectedJob.work_documents
                            })
                          });
                          if (response.ok) {
                            toast.success('İş bilgileri kaydedildi');
                          } else {
                            toast.error('Kaydetme başarısız');
                          }
                        } catch (error) {
                          toast.error('Kaydetme hatası');
                        }
                      }}
                      className="w-full"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Bilgileri Kaydet
                    </Button>
                  </div>
                </div>

                {/* Bekleyen İşlemler Listesi */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                    <ListChecks className="h-4 w-4" />
                    Bekleyen İşlemler ({pendingTransactions.length})
                    <span className="text-xs font-normal text-amber-600 ml-2">
                      (İşi tamamladığınızda kaydedilecek)
                    </span>
                  </h4>
                  
                  {pendingTransactions.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                      {pendingTransactions.map((tx) => (
                        <div key={tx.id} className="flex items-center justify-between bg-white p-3 rounded-lg border shadow-sm">
                          <div className="flex items-center gap-3">
                            <Badge className={
                              tx.type === 'giris' ? 'bg-green-100 text-green-700' : 
                              tx.type === 'cikis' ? 'bg-red-100 text-red-700' : 
                              tx.type === 'cek' ? 'bg-purple-100 text-purple-700' :
                              tx.type === 'alacak' ? 'bg-blue-100 text-blue-700' :
                              'bg-orange-100 text-orange-700'
                            }>
                              {tx.type === 'giris' ? 'Giriş' : 
                               tx.type === 'cikis' ? 'Çıkış' : 
                               tx.type === 'cek' ? 'Çek' : 
                               tx.type === 'alacak' ? 'Alacak' : 'Borç'}
                            </Badge>
                            <div>
                              <span className="font-semibold text-gray-800">
                                {tx.tutar?.toLocaleString('tr-TR')} ₺
                              </span>
                              <span className="text-xs text-gray-500 ml-2">{tx.tarih}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {tx.aciklama && (
                              <span className="text-xs text-gray-500 max-w-[120px] truncate mr-2" title={tx.aciklama}>
                                {tx.aciklama}
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => openEditTransactionModal(tx)}
                              title="Düzenle"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deletePendingTransaction(tx.id)}
                              title="Sil"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-amber-600 mb-3">Henüz işlem eklenmedi. Aşağıdaki butonları kullanarak işlem ekleyebilirsiniz.</p>
                  )}

                  {/* Toplam Özet */}
                  {pendingTransactions.length > 0 && (
                    <div className="flex gap-4 text-sm border-t border-amber-200 pt-2 mb-3">
                      {pendingTransactions.filter(t => t.type === 'giris').length > 0 && (
                        <span className="text-green-600">
                          <strong>Giriş:</strong> {pendingTransactions.filter(t => t.type === 'giris').reduce((a, b) => a + (b.tutar || 0), 0).toLocaleString('tr-TR')} ₺
                        </span>
                      )}
                      {pendingTransactions.filter(t => t.type === 'cikis').length > 0 && (
                        <span className="text-red-600">
                          <strong>Çıkış:</strong> {pendingTransactions.filter(t => t.type === 'cikis').reduce((a, b) => a + (b.tutar || 0), 0).toLocaleString('tr-TR')} ₺
                        </span>
                      )}
                      {pendingTransactions.filter(t => t.type === 'cek').length > 0 && (
                        <span className="text-purple-600">
                          <strong>Çek:</strong> {pendingTransactions.filter(t => t.type === 'cek').reduce((a, b) => a + (b.tutar || 0), 0).toLocaleString('tr-TR')} ₺
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* İşlem Ekleme Butonları - Orijinal sayfadaki modalları açar */}
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => handleJobAction(selectedJob, 'giris')}
                    className="bg-green-600 hover:bg-green-700"
                    size="sm"
                    data-testid="job-action-giris"
                  >
                    <DollarSign className="h-4 w-4 mr-1" />
                    Giriş Ekle
                  </Button>
                  <Button
                    onClick={() => handleJobAction(selectedJob, 'cikis')}
                    className="bg-red-600 hover:bg-red-700"
                    size="sm"
                    data-testid="job-action-cikis"
                  >
                    <CreditCard className="h-4 w-4 mr-1" />
                    Çıkış Ekle
                  </Button>
                  <Button
                    onClick={() => handleJobAction(selectedJob, 'cek')}
                    className="bg-purple-600 hover:bg-purple-700"
                    size="sm"
                    data-testid="job-action-cek"
                  >
                    <Receipt className="h-4 w-4 mr-1" />
                    Çek Ekle
                  </Button>
                  <Button
                    onClick={() => handleJobAction(selectedJob, 'alacak')}
                    className="bg-blue-600 hover:bg-blue-700"
                    size="sm"
                    data-testid="job-action-alacak"
                  >
                    <ArrowUp className="h-4 w-4 mr-1" />
                    Alacak Ekle
                  </Button>
                  <Button
                    onClick={() => handleJobAction(selectedJob, 'borc')}
                    className="bg-orange-600 hover:bg-orange-700"
                    size="sm"
                    data-testid="job-action-borc"
                  >
                    <ArrowDown className="h-4 w-4 mr-1" />
                    Borç Ekle
                  </Button>
                </div>
                
                {/* Tamamla Butonu */}
                <div className="pt-2 border-t">
                  <Button
                    onClick={() => setCompleteJobModalOpen(true)}
                    disabled={pendingTransactions.length === 0}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Hazırla ({pendingTransactions.length} işlem)
                  </Button>
                  {pendingTransactions.length === 0 && (
                    <p className="text-xs text-gray-500 text-center mt-1">Hazırlamak için en az bir işlem ekleyin</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Hazırlanan İşler için - Detaylar ve Yönetime Gönder */}
            {selectedJob?.status === 'prepared' && (
              <div className="w-full space-y-4 mb-3 pb-3 border-b">
                {/* Hazırlama Bilgileri */}
                {selectedJob.prepared_at && (
                  <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                    <h4 className="text-sm font-semibold text-cyan-700 mb-2 flex items-center gap-2">
                      <FileCheck className="h-4 w-4" />
                      Hazırlama Bilgileri
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Hazırlayan:</span>
                        <span className="ml-1 font-medium">{selectedJob.prepared_by_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Tarih:</span>
                        <span className="ml-1 font-medium">
                          {new Date(selectedJob.prepared_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {selectedJob.preparation_note && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Not:</span>
                          <span className="ml-1 font-medium">{selectedJob.preparation_note}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Eklenen İşlemler Listesi */}
                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                  <h4 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                    <ListChecks className="h-4 w-4" />
                    Eklenen İşlemler ({pendingTransactions.length})
                  </h4>
                  
                  {pendingTransactions.length > 0 ? (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto mb-3">
                      {pendingTransactions.map((tx) => {
                        const typeLabels = {
                          giris: 'Giriş', cikis: 'Çıkış', cek: 'Çek', alacak: 'Alacak', borc: 'Borç'
                        };
                        const typeColors = {
                          giris: 'bg-green-100 text-green-800 border-green-300',
                          cikis: 'bg-red-100 text-red-800 border-red-300',
                          cek: 'bg-purple-100 text-purple-800 border-purple-300',
                          alacak: 'bg-blue-100 text-blue-800 border-blue-300',
                          borc: 'bg-orange-100 text-orange-800 border-orange-300'
                        };
                        const fd = tx.form_data || {};
                        const faturaDurumLabels = {
                          fatura_alindi: 'Fatura Alındı', fatura_kesildi: 'Fatura Kesildi',
                          borc_verildi: 'Borç Verildi', borc_alindi: 'Borç Alındı',
                          fatura_kesilecek: 'Fatura Kesilecek', eksik_fatura: 'Eksik Fatura',
                          fis: 'Fiş', faturasiz_nakit: 'Faturasız Nakit', borc_odendi: 'Borç Ödendi'
                        };
                        const hesapTipiLabels = { banka_hesabi: 'Banka Hesabı', kredi_karti: 'Kredi Kartı', nakit_kasa: 'Nakit Kasa' };
                        const tutar = fd.miktar || fd.tutar || fd.fatura_miktari || tx.tutar || 0;
                        const paraBirimi = fd.para_birimi || 'TL';
                        const paraSimge = paraBirimi === 'TL' ? '₺' : paraBirimi === 'USD' ? '$' : paraBirimi === 'EUR' ? '€' : paraBirimi;
                        const empty = <span className="text-gray-400 italic">-</span>;
                        
                        return (
                          <div key={tx.id} className="bg-white rounded-lg border shadow-sm overflow-hidden">
                            {/* Başlık */}
                            <div className={`flex items-center justify-between px-4 py-3 border-b ${typeColors[tx.type] || 'bg-gray-100'}`}>
                              <div className="flex items-center gap-3">
                                <Badge className={`${typeColors[tx.type] || 'bg-gray-100'} text-sm px-3 py-1`}>
                                  {typeLabels[tx.type] || tx.type}
                                </Badge>
                                <span className="font-bold text-xl">
                                  {parseFloat(tutar).toLocaleString('tr-TR')} {paraSimge}
                                </span>
                              </div>
                              <span className="text-xs font-medium">
                                {tx.created_by_name && `${tx.created_by_name} · `}
                                {tx.created_at && new Date(tx.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            
                            {/* Giriş/Çıkış Form Alanları */}
                            {(tx.type === 'giris' || tx.type === 'cikis') && (
                              <div className="px-4 py-3">
                                <table className="w-full text-sm">
                                  <tbody>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium w-2/5">Tarih</td>
                                      <td className="py-2 font-medium">{fd.tarih || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Saat</td>
                                      <td className="py-2">{fd.saat || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Cari Hesap</td>
                                      <td className="py-2">{fd.cari_hesap_adi ? <span className="font-medium">{fd.cari_hesap_adi}</span> : empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Çek Karşılığı</td>
                                      <td className="py-2">{fd.cek_karsiligi ? <span className="text-purple-600 font-medium">Evet {fd.cek_no ? `- Çek No: ${fd.cek_no}` : ''}</span> : 'Hayır'}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">{tx.type === 'giris' ? 'Takipteki Alacak' : 'Takipteki Borç'}</td>
                                      <td className="py-2">{(tx.type === 'giris' ? fd.takipteki_alacak : fd.takipteki_borc) ? <span className="text-blue-600 font-medium">Evet</span> : 'Hayır'}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100 bg-amber-50">
                                      <td className="py-2 text-gray-500 font-medium">Miktar</td>
                                      <td className="py-2 font-bold text-base">{parseFloat(fd.miktar || 0).toLocaleString('tr-TR')} {paraSimge} {paraBirimi}</td>
                                    </tr>
                                    {fd.taksit_sayisi > 1 && (
                                      <tr className="border-b border-gray-100">
                                        <td className="py-2 text-gray-500 font-medium">Taksit Sayısı</td>
                                        <td className="py-2">{fd.taksit_sayisi} taksit</td>
                                      </tr>
                                    )}
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">{tx.type === 'giris' ? 'Paranın Yattığı Hesap' : 'Paranın Çıktığı Hesap'}</td>
                                      <td className="py-2">{fd.hesap_adi ? <>{fd.hesap_tipi && <span className="text-xs bg-gray-200 rounded px-1.5 py-0.5 mr-1">{hesapTipiLabels[fd.hesap_tipi] || fd.hesap_tipi}</span>}<span className="font-medium">{fd.hesap_adi}</span></> : empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Fortis Şube</td>
                                      <td className="py-2">{fd.fortis_sube || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Etiket (Hesap Kartı)</td>
                                      <td className="py-2">{fd.etiket_adi || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Fatura Durumu</td>
                                      <td className="py-2">{fd.fatura_durumu ? <span className="font-medium">{faturaDurumLabels[fd.fatura_durumu] || fd.fatura_durumu}</span> : empty}</td>
                                    </tr>
                                    {fd.fatura_durumu && (
                                      <>
                                        <tr className="border-b border-gray-100">
                                          <td className="py-2 text-gray-500 font-medium">Fatura Miktarı</td>
                                          <td className="py-2">{fd.fatura_miktari ? `${parseFloat(fd.fatura_miktari).toLocaleString('tr-TR')} ₺` : empty}</td>
                                        </tr>
                                        <tr className="border-b border-gray-100">
                                          <td className="py-2 text-gray-500 font-medium">KDV Tutarı</td>
                                          <td className="py-2">{fd.kdv_tutari ? `${parseFloat(fd.kdv_tutari).toLocaleString('tr-TR')} ₺` : empty}</td>
                                        </tr>
                                      </>
                                    )}
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Açıklama</td>
                                      <td className="py-2">{fd.aciklama || empty}</td>
                                    </tr>
                                    <tr>
                                      <td className="py-2 text-gray-500 font-medium">Belgeler</td>
                                      <td className="py-2">{fd.belgeler && fd.belgeler.length > 0 ? <div className="flex gap-1 flex-wrap">{fd.belgeler.map((b, i) => <a key={i} href={b.url || b} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded">{b.name || b.adi || `Belge ${i + 1}`}</a>)}</div> : empty}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Çek Form Alanları */}
                            {tx.type === 'cek' && (
                              <div className="px-4 py-3">
                                <table className="w-full text-sm">
                                  <tbody>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium w-2/5">Çek Tipi</td>
                                      <td className="py-2 font-medium">{fd.tip === 'alinan' ? 'Alınan Çek' : fd.tip === 'verilen' ? 'Verilen Çek' : empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Çek No</td>
                                      <td className="py-2 font-medium">{fd.cek_no || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100 bg-amber-50">
                                      <td className="py-2 text-gray-500 font-medium">Tutar</td>
                                      <td className="py-2 font-bold text-base">{parseFloat(fd.tutar || 0).toLocaleString('tr-TR')} ₺</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Cari Hesap</td>
                                      <td className="py-2">{fd.cari_hesap_adi || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Banka</td>
                                      <td className="py-2">{fd.banka || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Vade Tarihi</td>
                                      <td className="py-2">{fd.vade_tarihi || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Düzenleme Tarihi</td>
                                      <td className="py-2">{fd.duzenleme_tarihi || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Keşideci</td>
                                      <td className="py-2">{fd.kesideci || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Alıcı</td>
                                      <td className="py-2">{fd.alici || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Hesap Kartı</td>
                                      <td className="py-2">{fd.hesap_karti_adi || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Açıklama</td>
                                      <td className="py-2">{fd.aciklama || empty}</td>
                                    </tr>
                                    <tr>
                                      <td className="py-2 text-gray-500 font-medium">Belge</td>
                                      <td className="py-2">{fd.belge_url ? <a href={fd.belge_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded">{fd.belge_adi || 'Belge'}</a> : empty}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Alacak/Borç Form Alanları */}
                            {(tx.type === 'alacak' || tx.type === 'borc') && (
                              <div className="px-4 py-3">
                                <table className="w-full text-sm">
                                  <tbody>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium w-2/5">Cari Hesap</td>
                                      <td className="py-2">{fd.cari_hesap_adi || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100 bg-amber-50">
                                      <td className="py-2 text-gray-500 font-medium">Fatura Miktarı</td>
                                      <td className="py-2 font-bold text-base">{fd.fatura_miktari ? `${parseFloat(fd.fatura_miktari).toLocaleString('tr-TR')} ${paraSimge}` : empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Para Birimi</td>
                                      <td className="py-2">{fd.para_birimi || 'TL'}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Ödeme Sayısı</td>
                                      <td className="py-2">{fd.odeme_sayisi || 1}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Etiket (Hesap Kartı)</td>
                                      <td className="py-2">{fd.etiket_adi || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Fortis Şube</td>
                                      <td className="py-2">{fd.fortis_sube || empty}</td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 text-gray-500 font-medium">Açıklama</td>
                                      <td className="py-2">{fd.aciklama || empty}</td>
                                    </tr>
                                  </tbody>
                                </table>
                                {fd.odemeler && fd.odemeler.length > 0 && (
                                  <div className="mt-3 pt-2 border-t border-gray-200">
                                    <span className="text-sm text-gray-500 font-medium">Ödeme Planı:</span>
                                    <div className="mt-2 space-y-1">
                                      {fd.odemeler.map((odeme, idx) => (
                                        <div key={idx} className="flex items-center gap-3 text-sm bg-gray-50 px-3 py-2 rounded">
                                          <span className="font-medium text-gray-600">{idx + 1}.</span>
                                          <span className="font-medium">{odeme.tarih}</span>
                                          <span className="font-bold">{parseFloat(odeme.miktar || 0).toLocaleString('tr-TR')} ₺</span>
                                          {odeme.aciklama && <span className="text-gray-500">- {odeme.aciklama}</span>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-emerald-600">İşlem bilgisi bulunamadı.</p>
                  )}

                  {/* Toplam Özet */}
                  {pendingTransactions.length > 0 && (() => {
                    const getTutar = (t) => {
                      const fd = t.form_data || {};
                      return parseFloat(fd.miktar || fd.tutar || fd.fatura_miktari || t.tutar || 0);
                    };
                    return (
                      <div className="flex gap-4 text-sm border-t border-emerald-200 pt-2 font-medium">
                        {pendingTransactions.filter(t => t.type === 'giris').length > 0 && (
                          <span className="text-green-600">
                            Giriş: {pendingTransactions.filter(t => t.type === 'giris').reduce((a, b) => a + getTutar(b), 0).toLocaleString('tr-TR')} ₺
                          </span>
                        )}
                        {pendingTransactions.filter(t => t.type === 'cikis').length > 0 && (
                          <span className="text-red-600">
                            Çıkış: {pendingTransactions.filter(t => t.type === 'cikis').reduce((a, b) => a + getTutar(b), 0).toLocaleString('tr-TR')} ₺
                          </span>
                        )}
                        {pendingTransactions.filter(t => t.type === 'cek').length > 0 && (
                          <span className="text-purple-600">
                            Çek: {pendingTransactions.filter(t => t.type === 'cek').reduce((a, b) => a + getTutar(b), 0).toLocaleString('tr-TR')} ₺
                          </span>
                        )}
                        {pendingTransactions.filter(t => t.type === 'alacak').length > 0 && (
                          <span className="text-blue-600">
                            Alacak: {pendingTransactions.filter(t => t.type === 'alacak').reduce((a, b) => a + getTutar(b), 0).toLocaleString('tr-TR')} ₺
                          </span>
                        )}
                        {pendingTransactions.filter(t => t.type === 'borc').length > 0 && (
                          <span className="text-orange-600">
                            Borç: {pendingTransactions.filter(t => t.type === 'borc').reduce((a, b) => a + getTutar(b), 0).toLocaleString('tr-TR')} ₺
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {/* Yönetim Kurulu Onayla/Red Et butonları */}
                {isBoardView && (
                  <div className="flex gap-3 mt-4">
                    <Button
                      onClick={async () => {
                        if (!window.confirm(`"${selectedJob.konu}" işini onaylamak istiyor musunuz?`)) return;
                        try {
                          const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/board-approve`, {
                            method: 'PUT',
                            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ note: '' })
                          });
                          if (response.ok) {
                            toast.success('İş onaylandı ve Yapılacak İşlere aktarıldı');
                            setDetailModalOpen(false);
                            loadJobs();
                          } else {
                            const error = await response.json();
                            toast.error(error.detail || 'İşlem başarısız');
                          }
                        } catch (error) {
                          toast.error('İşlem sırasında bir hata oluştu');
                        }
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="board-approve-btn"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Onayla
                    </Button>
                    <Button
                      onClick={() => {
                        setRejectingJobId(selectedJob.id);
                        setRejectReason('');
                        setRejectModalOpen(true);
                      }}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      data-testid="board-reject-btn"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Red Et
                    </Button>
                  </div>
                )}
              </div>
            )}
            {selectedJob?.status === 'needs_correction' && (
              <Button 
                onClick={() => openCorrectionModal(selectedJob)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Düzelt
              </Button>
            )}
            {/* Yapılacak İşler için butonlar - Yönetim Kurulu Onayı */}
            {(selectedJob?.status === 'todo' || selectedJob?.status === 'new') && (
              <>
                <Button 
                  onClick={() => {
                    setTodoDocuments([]);
                    setAddDocumentsModalOpen(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Belgeler Ekle
                </Button>
                {/* Yönetim Kurulu Onay Butonu - pending_transactions varsa göster (avans işlerinde gizli) */}
                {!selectedJob?.is_avans_job && selectedJob?.pending_transactions && selectedJob.pending_transactions.length > 0 && (
                  <Button 
                    onClick={async () => {
                      if (!window.confirm(`Bu işi onaylamak istediğinize emin misiniz? ${selectedJob.pending_transactions.length} taslak işlem gerçek kayda dönüşecek.`)) return;
                      try {
                        const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/board-approve`, {
                          method: 'PUT',
                          headers: getAuthHeaders(),
                          body: JSON.stringify({ note: '' })
                        });
                        if (response.ok) {
                          const result = await response.json();
                          toast.success(result.message || 'İş onaylandı ve kayıtlar oluşturuldu');
                          setDetailModalOpen(false);
                          loadJobs();
                        } else {
                          const error = await response.json();
                          toast.error(error.detail || 'Onaylama başarısız');
                        }
                      } catch (error) {
                        toast.error('İşlem sırasında bir hata oluştu');
                      }
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Yönetim Onayla ({selectedJob.pending_transactions.length} işlem)
                  </Button>
                )}
                <Button 
                  onClick={handleCompleteTodoJob}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Tamamla
                </Button>
              </>
            )}
            {selectedJob?.status === 'pending' && (
              <div className="flex flex-col gap-3 w-full">
                {/* Onay Durumu Badge'leri */}
                <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">Muhasebe:</span>
                    {selectedJob.accounting_approved ? (
                      <Badge className="bg-green-100 text-green-700 border-green-300">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Onaylandı ({selectedJob.accounting_approved_by_name})
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
                        Bekliyor
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">Yönetim:</span>
                    {selectedJob.management_approved ? (
                      <Badge className="bg-green-100 text-green-700 border-green-300">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Onaylandı ({selectedJob.management_approved_by_name})
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
                        Bekliyor
                      </Badge>
                    )}
                  </div>
                </div>
                
                {/* Onay Butonları */}
                <div className="flex gap-2 flex-wrap">
                  {/* Muhasebe Onay Butonu - Sadece Muhasebe panelinde ve onay verilmemişse görünür */}
                  {!isBoardView && !selectedJob.accounting_approved && (
                    <Button 
                      onClick={async () => {
                        if (!window.confirm('Muhasebe onayı vermek istediğinize emin misiniz?')) return;
                        try {
                          const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/approve`, {
                            method: 'PUT',
                            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ approval_type: 'accounting' })
                          });
                          if (response.ok) {
                            toast.success('Muhasebe onayı verildi. Yönetim onayı bekleniyor.');
                            setDetailModalOpen(false);
                            loadPendingJobs();
                          } else {
                            const error = await response.json();
                            toast.error(error.detail || 'Onay başarısız');
                          }
                        } catch (error) {
                          toast.error('Onay sırasında bir hata oluştu');
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="accounting-approve-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Muhasebe Onayla
                    </Button>
                  )}

                  {/* Muhasebe Onayı Geri Al - Sadece Yönetim Kurulu panelinde + Muhasebe onaylanmış + Yönetim onaylanmamış */}
                  {isBoardView && selectedJob.accounting_approved && !selectedJob.management_approved && (
                    <Button 
                      variant="outline"
                      onClick={async () => {
                        if (!window.confirm('Muhasebe onayını geri almak istediğinize emin misiniz?')) return;
                        try {
                          const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/revoke-approval`, {
                            method: 'PUT',
                            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ approval_type: 'accounting' })
                          });
                          if (response.ok) {
                            toast.success('Muhasebe onayı geri alındı');
                            setSelectedJob(prev => ({
                              ...prev,
                              accounting_approved: false,
                              accounting_approved_by_name: null,
                              management_approved: false,
                              management_approved_by_name: null
                            }));
                            loadPendingJobs();
                          } else {
                            const error = await response.json();
                            toast.error(error.detail || 'İşlem başarısız');
                          }
                        } catch (error) {
                          toast.error('İşlem sırasında bir hata oluştu');
                        }
                      }}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Muhasebe Onayı Geri Al
                    </Button>
                  )}

                  {/* Yönetim Onay Butonu - Sadece Yönetim Kurulu panelinde + Muhasebe onaylanmış + henüz Yönetim onaylanmamış */}
                  {isBoardView && selectedJob.accounting_approved && !selectedJob.management_approved && (
                    <Button 
                      onClick={async () => {
                        if (!window.confirm('Yönetim onayı vermek istediğinize emin misiniz? İş "Onaylanan İşler"e taşınacak.')) return;
                        try {
                          const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/approve`, {
                            method: 'PUT',
                            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ approval_type: 'management' })
                          });
                          if (response.ok) {
                            toast.success('Yönetim onayı verildi. İş "Onaylanan İşler"e taşındı.');
                            setDetailModalOpen(false);
                            loadPendingJobs();
                            loadJobs();
                          } else {
                            const error = await response.json();
                            toast.error(error.detail || 'Onay başarısız');
                          }
                        } catch (error) {
                          toast.error('Onay sırasında bir hata oluştu');
                        }
                      }}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="management-approve-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Yönetim Onayla
                    </Button>
                  )}

                  {/* İptal Butonu - Muhasebe panelinde sadece onay verilmemişse; Yönetim panelinde her zaman */}
                  {((!isBoardView && !selectedJob.accounting_approved) || isBoardView) && (
                    <Button 
                      onClick={async () => {
                        if (!window.confirm('Bu işi iptal etmek istediğinize emin misiniz?')) return;
                        try {
                          const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/cancel`, {
                            method: 'PUT',
                            headers: getAuthHeaders()
                          });
                          if (response.ok) {
                            toast.success('İş iptal edildi');
                            setDetailModalOpen(false);
                            loadPendingJobs();
                            loadCancelledJobs();
                          } else {
                            const error = await response.json();
                            toast.error(error.detail || 'İptal işlemi başarısız');
                          }
                        } catch (error) {
                          toast.error('İptal işlemi sırasında bir hata oluştu');
                        }
                      }}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <X className="h-4 w-4 mr-2" />
                      İptal Et
                    </Button>
                  )}
                </div>
              </div>
            )}
            {/* Tamamlanan işler için Arşive Ekle butonu */}
            {selectedJob?.status === 'completed' && (
              <Button 
                onClick={async () => {
                  try {
                    const response = await fetch(`${API}/accounting/isler/${selectedJob.id}/archive`, {
                      method: 'PUT',
                      headers: getAuthHeaders()
                    });
                    if (response.ok) {
                      toast.success('İş arşive taşındı');
                      setDetailModalOpen(false);
                      loadJobs();
                      loadCompletedJobs();
                    } else {
                      const error = await response.json();
                      toast.error(error.detail || 'Arşivleme başarısız');
                    }
                  } catch (error) {
                    toast.error('Arşivleme sırasında bir hata oluştu');
                  }
                }}
                className="bg-gray-600 hover:bg-gray-700"
              >
                <Archive className="h-4 w-4 mr-2" />
                Arşive Ekle
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Düzeltme Modalı */}
      <Dialog open={correctionModalOpen} onOpenChange={setCorrectionModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Düzeltme Gönder
            </DialogTitle>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-4 py-4">
              {/* Geçmiş Yazışmalar */}
              {selectedJob.corrections && selectedJob.corrections.length > 0 && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Geçmiş Yazışmalar ({selectedJob.corrections.length})
                  </h3>
                  <div className="space-y-3 max-h-[250px] overflow-y-auto">
                    {selectedJob.corrections.map((correction, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${
                          correction.tip === 'duzeltme_gonderildi' 
                            ? 'bg-blue-50 border-blue-200' 
                            : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className={`text-xs font-medium ${
                            correction.tip === 'duzeltme_gonderildi' 
                              ? 'text-blue-600' 
                              : 'text-amber-600'
                          }`}>
                            {correction.tip === 'duzeltme_gonderildi' 
                              ? `✓ Düzeltme Gönderildi - ${correction.submitted_by_name || 'Muhasebe'}`
                              : `⚠ Düzeltme Talebi - ${correction.requested_by_name || 'Yönetim Kurulu'}`
                            }
                          </span>
                          <span className="text-xs text-gray-500">
                            {correction.tarih ? new Date(correction.tarih).toLocaleDateString('tr-TR', { 
                              day: '2-digit', month: '2-digit', year: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            }) : '-'}
                          </span>
                        </div>
                        {correction.konu && (
                          <p className="text-sm font-medium text-gray-800 mb-1">Konu: {correction.konu}</p>
                        )}
                        <p className="text-sm text-gray-700">{correction.aciklama}</p>
                        
                        {correction.belgeler && correction.belgeler.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <span className="text-xs text-gray-500">Belgeler:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {correction.belgeler.map((belge, belgeIdx) => (
                                <div key={belgeIdx} className="flex items-center gap-1 bg-white px-2 py-1 rounded text-xs border">
                                  <File className="h-3 w-3" />
                                  <span>{belge.name}</span>
                                  {belge.url && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0"
                                      onClick={() => window.open(belge.url, '_blank')}
                                    >
                                      <Eye className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Orijinal İş Bilgileri */}
              <div className="border rounded-lg p-4 bg-white">
                <h3 className="font-semibold text-gray-700 mb-3">Orijinal İş Bilgileri</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500 text-xs">Oluşturulma</Label>
                    <p className="text-sm">{selectedJob.created_at ? new Date(selectedJob.created_at).toLocaleDateString('tr-TR') : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Şube</Label>
                    <p className="text-sm">{selectedJob.sube_adi || '-'}</p>
                  </div>
                </div>
                {selectedJob.belgeler && selectedJob.belgeler.length > 0 && (
                  <div className="mt-3">
                    <Label className="text-gray-500 text-xs">Mevcut Belgeler ({selectedJob.belgeler.length})</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedJob.belgeler.map((belge, idx) => (
                        <div key={idx} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-xs">
                          <File className="h-3 w-3" />
                          <span>{belge.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Düzeltme Formu */}
              <div className="border-2 border-amber-300 rounded-lg p-4 bg-amber-50">
                <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Düzeltilmiş Bilgiler
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="correction-konu" className="text-amber-800">
                      İşin Konusu <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="correction-konu"
                      value={correctionFormData.konu}
                      onChange={(e) => setCorrectionFormData(prev => ({ ...prev, konu: e.target.value }))}
                      placeholder="Düzeltilmiş iş konusu"
                      className="mt-1 bg-white"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="correction-aciklama" className="text-amber-800">
                      Açıklama / Düzeltme Notu
                    </Label>
                    <Textarea
                      id="correction-aciklama"
                      value={correctionFormData.aciklama}
                      onChange={(e) => setCorrectionFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                      placeholder="Yapılan düzeltmeler hakkında açıklama..."
                      rows={3}
                      className="mt-1 bg-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-amber-800">Ek Belgeler</Label>
                    <div className="mt-2">
                      <label className="cursor-pointer">
                        <div className="border-2 border-dashed border-amber-400 rounded-lg p-4 text-center hover:bg-amber-100 transition-colors bg-white">
                          <Upload className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                          <span className="text-sm text-amber-700">
                            Dosya yüklemek için tıklayın
                          </span>
                          <p className="text-xs text-gray-500 mt-1">PDF, Word, Excel, Resim</p>
                        </div>
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                          onChange={handleCorrectionFileUpload}
                        />
                      </label>
                    </div>
                    
                    {correctionFormData.belgeler.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {correctionFormData.belgeler.map((belge, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                            <div className="flex items-center gap-2">
                              <File className="h-4 w-4 text-amber-600" />
                              <span className="text-sm">{belge.name}</span>
                              <span className="text-xs text-gray-400">
                                ({(belge.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeCorrectionFile(idx)}
                              className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCorrectionModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSubmitCorrection} 
              disabled={correctionSubmitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {correctionSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Düzeltmeyi Gönder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İşi Hazırla Modalı */}
      <Dialog open={completeJobModalOpen} onOpenChange={setCompleteJobModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              İşi Hazırla
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <p className="text-sm text-emerald-700 mb-2">
                <strong>{selectedJob?.konu || selectedJob?.work_title}</strong> işini hazırlamak üzeresiniz.
              </p>
              <p className="text-xs text-emerald-600">
                İş "Hazırlanan İşler" sekmesine aktarılacak ve Yönetim Kurulu onayına sunulacaktır. İşlemler, Yönetim Kurulu onayladıktan sonra veritabanına kaydedilecektir.
              </p>
            </div>
            
            {/* Bekleyen İşlemler Özeti */}
            {pendingTransactions.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg border">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <ListChecks className="h-4 w-4" />
                  Hazırlanacak İşlemler ({pendingTransactions.length})
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {pendingTransactions.map((tx, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border">
                      <Badge className={
                        tx.type === 'giris' ? 'bg-green-100 text-green-700' : 
                        tx.type === 'cikis' ? 'bg-red-100 text-red-700' : 
                        tx.type === 'cek' ? 'bg-purple-100 text-purple-700' :
                        tx.type === 'alacak' ? 'bg-blue-100 text-blue-700' :
                        'bg-orange-100 text-orange-700'
                      }>
                        {tx.type === 'giris' ? 'Giriş' : 
                         tx.type === 'cikis' ? 'Çıkış' : 
                         tx.type === 'cek' ? 'Çek' : 
                         tx.type === 'alacak' ? 'Alacak' : 'Borç'}
                      </Badge>
                      <span className="font-medium">{tx.tutar?.toLocaleString('tr-TR')} ₺</span>
                      <span className="text-gray-500 text-xs">{tx.tarih}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 text-sm border-t pt-3 mt-3">
                  {pendingTransactions.filter(t => t.type === 'giris').length > 0 && (
                    <span className="text-green-600">
                      <strong>Toplam Giriş:</strong> {pendingTransactions.filter(t => t.type === 'giris').reduce((a, b) => a + (b.tutar || 0), 0).toLocaleString('tr-TR')} ₺
                    </span>
                  )}
                  {pendingTransactions.filter(t => t.type === 'cikis').length > 0 && (
                    <span className="text-red-600">
                      <strong>Toplam Çıkış:</strong> {pendingTransactions.filter(t => t.type === 'cikis').reduce((a, b) => a + (b.tutar || 0), 0).toLocaleString('tr-TR')} ₺
                    </span>
                  )}
                </div>
              </div>
            )}
            
            <div>
              <Label>Not (Opsiyonel)</Label>
              <Textarea
                value={completeJobNote}
                onChange={(e) => setCompleteJobNote(e.target.value)}
                placeholder="İş hakkında notlarınız..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteJobModalOpen(false)}>İptal</Button>
            <Button 
              onClick={completeApprovedJob} 
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={pendingTransactions.length === 0}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Hazırla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yapılacak İşler için Belge Ekleme Modalı */}
      <Dialog open={addDocumentsModalOpen} onOpenChange={setAddDocumentsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Belge Ekle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedJob && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>İş:</strong> {selectedJob.konu}
                </p>
              </div>
            )}
            
            {/* Dosya Yükleme Alanı */}
            <div>
              <Label>Belgeler</Label>
              <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleTodoDocumentUpload}
                  className="hidden"
                  id="todo-document-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <label htmlFor="todo-document-upload" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Dosya seçmek için tıklayın</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Resim dosyaları</p>
                </label>
              </div>
            </div>
            
            {/* Yüklenen Belgeler Listesi */}
            {todoDocuments.length > 0 && (
              <div className="space-y-2">
                <Label>Eklenecek Belgeler ({todoDocuments.length})</Label>
                <div className="max-h-[200px] overflow-y-auto space-y-2">
                  {todoDocuments.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-600" />
                        <span className="text-sm truncate max-w-[300px]">{doc.name}</span>
                        <span className="text-xs text-gray-400">
                          ({(doc.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTodoDocuments(prev => prev.filter((_, i) => i !== index))}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDocumentsModalOpen(false)}>İptal</Button>
            <Button 
              onClick={handleAddTodoDocuments}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={todoDocuments.length === 0 || todoDocumentsSubmitting}
            >
              {todoDocumentsSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Belgeleri Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İşlem Ekleme Modalı (Taslak olarak kaydedilir) */}
      <Dialog open={jobActionModal.open} onOpenChange={(open) => !open && setJobActionModal({ open: false, type: null })}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {jobActionModal.type === 'giris' && <DollarSign className="h-5 w-5 text-green-600" />}
              {jobActionModal.type === 'cikis' && <CreditCard className="h-5 w-5 text-red-600" />}
              {jobActionModal.type === 'cek' && <Receipt className="h-5 w-5 text-purple-600" />}
              {jobActionModal.type === 'alacak' && <ArrowUp className="h-5 w-5 text-blue-600" />}
              {jobActionModal.type === 'borc' && <ArrowDown className="h-5 w-5 text-orange-600" />}
              {editingTransaction ? 'İşlem Düzenle' : 
                jobActionModal.type === 'giris' ? 'Giriş Ekle (Taslak)' :
                jobActionModal.type === 'cikis' ? 'Çıkış Ekle (Taslak)' :
                jobActionModal.type === 'cek' ? 'Çek Ekle (Taslak)' :
                jobActionModal.type === 'alacak' ? 'Alacak Ekle (Taslak)' :
                'Borç Ekle (Taslak)'}
            </DialogTitle>
            <DialogDescription>
              {selectedJob?.konu || selectedJob?.work_title} işi için {editingTransaction ? 'işlem düzenleniyor' : 'taslak işlem ekleniyor'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tarih *</Label>
                <Input
                  type="date"
                  value={jobActionData.tarih}
                  onChange={(e) => setJobActionData({...jobActionData, tarih: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Tutar (₺) *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={jobActionData.tutar}
                  onChange={(e) => setJobActionData({...jobActionData, tutar: e.target.value})}
                />
              </div>
            </div>
            
            {(jobActionModal.type === 'cek' || jobActionModal.type === 'alacak' || jobActionModal.type === 'borc') && (
              <div className="space-y-2">
                <Label>Vade Tarihi</Label>
                <Input
                  type="date"
                  value={jobActionData.vade_tarihi}
                  onChange={(e) => setJobActionData({...jobActionData, vade_tarihi: e.target.value})}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Cari Hesap</Label>
              <Input
                placeholder="Cari hesap adı"
                value={jobActionData.cari_hesap_adi}
                onChange={(e) => setJobActionData({...jobActionData, cari_hesap_adi: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Hesap</Label>
              <Input
                placeholder="Hesap adı"
                value={jobActionData.hesap_adi}
                onChange={(e) => setJobActionData({...jobActionData, hesap_adi: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                placeholder="İşlem açıklaması..."
                value={jobActionData.aciklama}
                onChange={(e) => setJobActionData({...jobActionData, aciklama: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setJobActionModal({ open: false, type: null })}>
              İptal
            </Button>
            <Button 
              onClick={editingTransaction ? updatePendingTransaction : addPendingTransaction}
              disabled={jobActionSubmitting}
              className={
                jobActionModal.type === 'giris' ? 'bg-green-600 hover:bg-green-700' :
                jobActionModal.type === 'cikis' ? 'bg-red-600 hover:bg-red-700' :
                jobActionModal.type === 'cek' ? 'bg-purple-600 hover:bg-purple-700' :
                jobActionModal.type === 'alacak' ? 'bg-blue-600 hover:bg-blue-700' :
                'bg-orange-600 hover:bg-orange-700'
              }
            >
              {jobActionSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              {editingTransaction ? 'Güncelle' : 'Taslak Olarak Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Red Et Modalı */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="h-5 w-5" />
              İşi Red Et
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="reject-reason" className="text-sm font-medium">
                Red Açıklaması *
              </Label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Red nedenini yazın..."
                rows={4}
                className="mt-1"
                data-testid="reject-reason-textarea"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              İptal
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={!rejectReason.trim()}
              data-testid="reject-confirm-btn"
              onClick={async () => {
                try {
                  const response = await fetch(`${API}/accounting/isler/${rejectingJobId}/board-reject`, {
                    method: 'PUT',
                    headers: {
                      ...getAuthHeaders(),
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ note: rejectReason.trim() })
                  });
                  if (response.ok) {
                    toast.success('İş reddedildi');
                    setRejectModalOpen(false);
                    setDetailModalOpen(false);
                    loadJobs();
                  } else {
                    const error = await response.json();
                    toast.error(error.detail || 'İşlem başarısız');
                  }
                } catch (error) {
                  toast.error('İşlem sırasında bir hata oluştu');
                }
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Red Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AccountingJobs;
